import { G as HandoffStatus, x as getCommonTrackingEventFields, w as trackCopilotInteractionEvent, m as makeClientTool, H as HANDOFF_TOOL_NAME, h as commonToolParams, o as createCopilotRegistry, d as createOnToolCall, s as serializeTools, S as SPAN_ID_HEADERS, e as extractComponents, F as FEEDBACK_RATINGS, I as getBraintrustLogsUrl } from './responseHelpers-CCMrXwFJ.mjs';
export { f as createErrorObject, g as createServerTools, l as createStreamResponseWithHeaders, n as extractSpanIdForTracing, z as getAssetType, A as getCharacterQuantity, q as getGlobalCopilotRegistry, i as getTraceSpanId, y as getUserRating, p as globalCopilots, j as isNewUserMessage, a as makeFinalTool, b as makeServerTool, c as makeToolComponent, r as recurseTrim, k as setSpanIdHeaders, v as trackCopilotPredictionOutputEvent, t as trackCopilotToolExecutedAttemptEvent, u as trackCopilotUserFeedbackEvent } from './responseHelpers-CCMrXwFJ.mjs';
import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext, forwardRef, useImperativeHandle, useLayoutEffect, useId } from 'react';
import { z } from 'zod';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Button, Alert, AlertDescription, IconButton, Icon as Icon$1, Text, Link, Tag, Spinner } from '@zapier/design-system';
import { Tooltip, FilterChipList, FilterChip, FilterChipIcon } from '@zapier/design-system-beta';
import clsx$1, { clsx } from 'clsx';
import { toLower, snakeCase } from 'lodash';
import { Icon } from '@zapier/zinnia-icons';
import { FileTrigger, ToggleButtonGroup, ToggleButton, Section, Header as Header$1, MenuItem as MenuItem$1, Separator as Separator$1, Popover, Menu as Menu$1, MenuTrigger as MenuTrigger$1 } from 'react-aria-components';
import { useChat } from '@ai-sdk/react';
import { flushSync } from 'react-dom';
import { C as CopilotMode, F as FeedbackValue, a as FeedbackType, b as FeedbackSentiment } from './types-DJw4fOmh.mjs';
import Markdown from 'markdown-to-jsx';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

function ToolPart({
  part,
  components,
  message,
  addToolResult,
  FallbackToolPart = ({ part: part2 }) => /* @__PURE__ */ jsx("pre", { children: JSON.stringify(part2.toolInvocation, null, 2) }),
  ...props
}) {
  const result = part.toolInvocation.state === "result" ? part.toolInvocation.result : void 0;
  const Component = components[part.toolInvocation.toolName];
  if (Component) {
    return /* @__PURE__ */ jsx(
      Component,
      {
        args: part.toolInvocation.args,
        result,
        message,
        part,
        addToolResult,
        ...props
      }
    );
  }
  return /* @__PURE__ */ jsx(FallbackToolPart, { part, ...props });
}

const HandoffSuggestion = ({
  buttonText,
  onButtonClick
}) => {
  return /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Button, { variant: "primary", size: "compact", onClick: onButtonClick, children: buttonText }) });
};

const SCHEMA_VERSIONS = {
  CURRENT: 1
};
function payloadToSchema(payload, metadata) {
  return {
    version: SCHEMA_VERSIONS.CURRENT,
    createdAt: (/* @__PURE__ */ new Date()).toISOString(),
    conversationId: payload.conversationId,
    payload: {
      messages: payload.messages,
      handoffData: payload.handoffData,
      fromCopilotId: payload.fromCopilotId,
      toCopilotId: payload.toCopilotId,
      continueMessage: payload.continueMessage
    },
    metadata: {
      ...metadata,
      storageMethod: metadata?.storageMethod || "other"
    }
  };
}
function schemaToPayload(schema) {
  return {
    timestamp: new Date(schema.createdAt).getTime(),
    messages: schema.payload.messages,
    handoffData: schema.payload.handoffData,
    conversationId: schema.conversationId,
    fromCopilotId: schema.payload.fromCopilotId,
    toCopilotId: schema.payload.toCopilotId,
    continueMessage: schema.payload.continueMessage
  };
}
function validateSchema(schema) {
  if (!schema || typeof schema !== "object") {
    return false;
  }
  const s = schema;
  if (typeof s.version !== "number" || typeof s.conversationId !== "string") {
    return false;
  }
  if (s.version === SCHEMA_VERSIONS.CURRENT) {
    return !!(typeof s.createdAt === "string" && s.payload && Array.isArray(s.payload.messages) && typeof s.payload.fromCopilotId === "string" && typeof s.payload.toCopilotId === "string" && typeof s.payload.continueMessage === "string");
  }
  return false;
}

const urlHashHandoffStorage = {
  async save(conversationId, payload) {
    if (typeof window === "undefined") {
      return;
    }
    try {
      const schema = payloadToSchema(payload, {
        storageMethod: "urlHash",
        client: typeof window !== "undefined" ? { userAgent: window.navigator.userAgent } : void 0
      });
      const jsonSchema = JSON.stringify(schema);
      const encodedPayload = Buffer.from(
        encodeURIComponent(jsonSchema),
        "utf8"
      ).toString("base64");
      const hashData = {
        [conversationId]: encodedPayload
      };
      const existingHash = window.location.hash.slice(1);
      let existingData = {};
      if (existingHash) {
        try {
          existingData = JSON.parse(decodeURIComponent(existingHash));
        } catch {
          existingData = {};
        }
      }
      const newHashData = { ...existingData, ...hashData };
      window.location.hash = encodeURIComponent(JSON.stringify(newHashData));
    } catch (error) {
      console.error("Failed to save handoff to URL hash:", error);
      throw new Error("Failed to save handoff data to URL");
    }
  },
  async createHandoffUrl(targetUrl, conversationId, payload) {
    return createHandoffUrl(targetUrl, conversationId, payload);
  },
  async load(conversationId) {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      const hash = window.location.hash.slice(1);
      if (!hash) {
        return null;
      }
      const hashData = JSON.parse(decodeURIComponent(hash));
      const encodedPayload = hashData[conversationId];
      if (!encodedPayload) {
        return null;
      }
      const jsonSchema = decodeURIComponent(
        Buffer.from(encodedPayload, "base64").toString("utf8")
      );
      const schema = JSON.parse(jsonSchema);
      if (!validateSchema(schema)) {
        console.error("Invalid handoff schema in URL hash");
        return null;
      }
      const payload = schemaToPayload(schema);
      return payload;
    } catch (error) {
      console.error("Failed to load handoff from URL hash:", error);
      return null;
    }
  },
  async cleanupAfterApplyHandoff(conversationId) {
    clearHandoffFromHash(conversationId);
  }
};
function clearHandoffFromHash(conversationId) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const hash = window.location.hash.slice(1);
    if (!hash) {
      return;
    }
    const hashData = JSON.parse(decodeURIComponent(hash));
    delete hashData[conversationId];
    if (Object.keys(hashData).length === 0) {
      window.location.hash = "";
    } else {
      window.location.hash = encodeURIComponent(JSON.stringify(hashData));
    }
  } catch (error) {
    console.error("Failed to clear handoff from URL hash:", error);
  }
}
async function createHandoffUrl(targetUrl, conversationId, payload) {
  try {
    const schema = payloadToSchema(payload, { storageMethod: "urlHash" });
    const jsonSchema = JSON.stringify(schema);
    const encodedPayload = Buffer.from(
      encodeURIComponent(jsonSchema),
      "utf8"
    ).toString("base64");
    const hashData = {
      [conversationId]: encodedPayload
    };
    const url = new URL(targetUrl);
    url.searchParams.set("conversationId", conversationId);
    url.hash = encodeURIComponent(JSON.stringify(hashData));
    return url.toString();
  } catch (error) {
    console.error("Failed to create handoff URL:", error);
    throw new Error("Failed to create handoff URL");
  }
}

const ucapiChatStorage = {
  save: async (conversationId, payload) => {
    return urlHashHandoffStorage.save(conversationId, {
      ...payload,
      messages: []
    });
  },
  load: async (conversationId) => {
    return urlHashHandoffStorage.load(conversationId);
  },
  createHandoffUrl: async (targetUrl, conversationId, payload) => {
    return urlHashHandoffStorage.createHandoffUrl(targetUrl, conversationId, {
      ...payload,
      messages: []
    });
  },
  cleanupAfterApplyHandoff: async (conversationId) => {
    return await urlHashHandoffStorage.cleanupAfterApplyHandoff(conversationId);
  }
};
const defaultHandoffStorage = ucapiChatStorage;

const ADDITIONAL_HANDOFF_DATA_STRING = "<additionalHandoffData>";
const ADDITIONAL_HANDOFF_DATA_STRING_END = "</additionalHandoffData>";
const HANDOFF_DATA_MESSAGE_ID_PREFIX = "handoff-data-";
function useHandoff({
  copilotId,
  conversationId,
  registry,
  storage,
  onHandoffReceived
}) {
  const [handoffPayload, setHandoffPayload] = useState(null);
  const [handoffStatus, setHandoffStatus] = useState(
    HandoffStatus.None
  );
  useEffect(() => {
    const loadHandoffData = async () => {
      setHandoffStatus(HandoffStatus.Loading);
      try {
        const data = await storage.load(conversationId);
        if (data) {
          setHandoffPayload(data);
          setHandoffStatus(HandoffStatus.Ready);
          onHandoffReceived?.(data);
        } else {
          setHandoffStatus(HandoffStatus.None);
        }
      } catch (error) {
        console.error("Failed to load handoff data:", error);
        setHandoffStatus(HandoffStatus.None);
      }
    };
    loadHandoffData();
  }, [conversationId, storage, onHandoffReceived]);
  const performHandoff = useCallback(
    async (request) => {
      const payload = {
        ...request,
        conversationId,
        messages: request.messages,
        handoffData: request.getHandoffData?.(),
        fromCopilotId: copilotId,
        timestamp: Date.now()
      };
      await storage.save(conversationId, payload);
      const handoffUrl = await storage.createHandoffUrl(
        request.targetUrl,
        conversationId,
        payload
      );
      const commonFields = getCommonTrackingEventFields({
        conversationId,
        copilotId,
        userMessageId: request.messages?.slice().reverse().find((m) => m.role === "user")?.id
        // TODO: Add customUserId and accountId in EAI-1766
      });
      trackCopilotInteractionEvent({
        interaction_name: "initiate_handoff",
        interaction_goal: `initiate_${request.toCopilotId}_handoff`,
        interaction_raw_text: `Continue with ${request.toCopilotId}`,
        event_action: "click",
        subject: "handoff",
        ...commonFields
      });
      window.location.href = handoffUrl;
    },
    [copilotId, registry, storage, conversationId]
  );
  const applyHandoff = useCallback(
    async (initialMessagesOfRecipientCopilot, existingMessages, setMessages, append) => {
      if (handoffStatus === HandoffStatus.Ready && handoffPayload) {
        setHandoffStatus(HandoffStatus.Applied);
        const handoffDataMessages = [];
        if (handoffPayload.handoffData) {
          const handoffDataText = `
          ${ADDITIONAL_HANDOFF_DATA_STRING}
          Handoff data from ${handoffPayload.fromCopilotId}:
          ${JSON.stringify(handoffPayload.handoffData)}
          ${ADDITIONAL_HANDOFF_DATA_STRING_END}
          `;
          handoffDataMessages.push({
            id: `${HANDOFF_DATA_MESSAGE_ID_PREFIX}${Date.now()}${handoffPayload.fromCopilotId}`,
            role: "assistant",
            content: handoffDataText,
            parts: [{ type: "text", text: handoffDataText }]
          });
        }
        setMessages([
          ...existingMessages,
          ...handoffDataMessages,
          ...initialMessagesOfRecipientCopilot || []
        ]);
        if (append && handoffPayload.continueMessage) {
          append({
            role: "user",
            content: handoffPayload.continueMessage
          });
        }
        await storage.cleanupAfterApplyHandoff?.(conversationId);
      }
    },
    [handoffPayload, handoffStatus, storage, conversationId]
  );
  return {
    performHandoff,
    applyHandoff,
    handoffStatus,
    handoffPayload
  };
}

function createDefaultHandoffComponent() {
  const DefaultHandoffComponent = ({
    args,
    result,
    messages,
    getHandoffData,
    conversationId,
    currentCopilotId,
    registry
  }) => {
    const { performHandoff } = useHandoff({
      copilotId: currentCopilotId,
      conversationId,
      registry,
      storage: defaultHandoffStorage
    });
    if (!args || !args.toCopilot?.id) return null;
    const targetCopilot = registry.getById(args.toCopilot.id);
    if (!targetCopilot) return null;
    if (!result?.targetUrl) return null;
    const handleHandoff = async () => {
      await performHandoff({
        messages: messages || [],
        getHandoffData,
        toCopilotId: args.toCopilot.id,
        continueMessage: args.continueMessage,
        targetUrl: result.targetUrl
      });
    };
    return /* @__PURE__ */ jsx(
      HandoffSuggestion,
      {
        buttonText: `Continue with ${targetCopilot.name}`,
        onButtonClick: handleHandoff
      }
    );
  };
  return DefaultHandoffComponent;
}

const baseHandoffParams = commonToolParams.extend({
  continueMessage: z.string().describe(
    "A short, self-contained user message that expresses what the user wants to accomplish next in the target copilot. Write it as the user would naturally continue the conversation with their specific request or goal."
  )
});
function createHandoffRequestSchema(registry) {
  const copilotSchemas = registry.copilots.map((copilot) => {
    if (copilot.parameters) {
      return z.object({
        id: z.literal(copilot.id),
        parameters: copilot.parameters
      });
    } else {
      return z.object({
        id: z.literal(copilot.id)
      });
    }
  });
  let toCopilotSchema;
  if (copilotSchemas.length === 0) {
    throw new Error("Registry must contain at least one copilot");
  } else if (copilotSchemas.length === 1) {
    toCopilotSchema = copilotSchemas[0];
  } else {
    toCopilotSchema = z.discriminatedUnion(
      "id",
      copilotSchemas
    );
  }
  return baseHandoffParams.extend({
    toCopilot: toCopilotSchema
  });
}
const jsonCopy = (obj) => JSON.parse(JSON.stringify(obj));
function makeHandoffTool({
  registry,
  fromCopilotId,
  component
}) {
  const HandoffRequestSchema = createHandoffRequestSchema(registry);
  const componentToUse = component || createDefaultHandoffComponent();
  const description = [
    "Hand off the conversation to another specialized copilot assistant. Available copilots:",
    ...registry.copilots.map(
      (c) => `- ${c.id}: ${c.description}${c.parameters ? " (requires parameters)" : ""}`
    ),
    "When you use this the user will be presented with a link to open the destination copilot.",
    "Before calling this tool, simply explain that they can open the destination copilot to carry on.",
    "Before calling this tool, also include any helpful information to the user. No text will be sent to the user after this tool is executed."
  ].join("\n");
  return makeClientTool({
    name: HANDOFF_TOOL_NAME,
    description,
    parameters: HandoffRequestSchema,
    execute: async ({ toCopilot }) => {
      const fromCopilot = registry.getById(fromCopilotId);
      const targetCopilot = registry.getById(toCopilot.id);
      if (!targetCopilot) {
        throw new Error(`Target copilot ${toCopilot.id} not found in registry`);
      }
      const parameters = toCopilot.parameters || {};
      if (targetCopilot.parameters) {
        try {
          targetCopilot.parameters.parse(parameters);
        } catch (error) {
          throw new Error(`Invalid parameters for ${toCopilot.id}: ${error}`);
        }
      }
      const targetUrl = targetCopilot.generateUrl(parameters);
      return jsonCopy({
        fromCopilot: { ...fromCopilot, parameters: void 0 },
        toCopilot: { ...targetCopilot, parameters: void 0 },
        targetUrl
      });
    },
    component: ({ args, result, messages, getHandoffData, conversationId }) => {
      return React.createElement(componentToUse, {
        args,
        result,
        messages,
        getHandoffData,
        conversationId,
        currentCopilotId: fromCopilotId,
        registry
      });
    }
  });
}

function generateRandomUUID() {
  return crypto.randomUUID();
}

function useConversationId() {
  return useMemo(() => {
    const uuid = generateRandomUUID();
    if (typeof window === "undefined") {
      return uuid;
    }
    try {
      const conversationIdFromUrl = getConversationIdFromUrl();
      if (conversationIdFromUrl) {
        return conversationIdFromUrl;
      }
      return uuid;
    } catch (error) {
      console.warn(
        "Failed to parse conversation ID from URL, generating new one:",
        error
      );
      return uuid;
    }
  }, []);
}
function getConversationIdFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const existingId = urlParams.get("conversationId");
  if (!existingId || existingId === "undefined" || existingId === "null" || existingId.trim() === "") {
    return null;
  }
  return existingId;
}

function makeToolEffect(effect) {
  return effect;
}
function processToolEffects(options) {
  const { messages, effects, processedInvocations } = options;
  const effectMap = Object.fromEntries(effects.map((e) => [e.name, e.effect]));
  messages.forEach((message) => {
    if (message.role !== "assistant") return;
    message.parts.forEach((part) => {
      if (part.type !== "tool-invocation") return;
      if (part.toolInvocation.state !== "result") return;
      const invocation = part.toolInvocation;
      const invocationId = invocation.toolCallId;
      const effect = effectMap[invocation.toolName];
      const shouldRunEffect = !processedInvocations.has(invocationId) && effect;
      if (!shouldRunEffect) return;
      processedInvocations.add(invocationId);
      Promise.resolve(
        effect({
          args: invocation.args,
          result: invocation.result
        })
      ).catch((error) => {
        console.error(`Effect error for ${invocation.toolName}:`, error);
      });
    });
  });
}
function useToolEffects(options) {
  const { messages, effects, status, conversationId, copilotId } = options;
  const processedInvocationsRef = useRef(/* @__PURE__ */ new Set());
  const processingRef = useRef(false);
  const effectsRef = useRef(effects);
  effectsRef.current = effects;
  useEffect(() => {
    if (processingRef.current) return;
    if (status === "streaming" || status === "submitted") {
      processingRef.current = true;
    }
  }, [status]);
  useEffect(() => {
    if (!processingRef.current) return;
    processToolEffects({
      messages,
      effects: effectsRef.current,
      processedInvocations: processedInvocationsRef.current});
  }, [messages]);
}
const useHandoffStreamStopping = ({
  messages,
  status,
  stop
}) => {
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === "assistant" && lastMessage.parts) {
      const hasHandoffTool = lastMessage.parts.some(
        (part) => part.type === "tool-invocation" && part.toolInvocation.toolName === HANDOFF_TOOL_NAME && part.toolInvocation.state === "result"
        // Only stop when tool has executed
      );
      if (hasHandoffTool && (status === "streaming" || status === "submitted")) {
        stop();
      }
    }
  }, [messages, status, stop]);
};

const alertContainer = "_alertContainer_1xy3l_1";
const actionsContainer$1 = "_actionsContainer_1xy3l_6";
const styles$l = {
	alertContainer: alertContainer,
	actionsContainer: actionsContainer$1
};

const CopilotAlert = ({
  title = "Copilot isn't responding right now",
  message = "You haven't lost progress. Try again to continue.",
  actions = [],
  status,
  className
}) => {
  return /* @__PURE__ */ jsx("div", { className: clsx(styles$l.alertContainer, className), children: /* @__PURE__ */ jsxs(Alert, { title, status, variant: "alert", children: [
    /* @__PURE__ */ jsx(AlertDescription, { children: message }),
    /* @__PURE__ */ jsx("div", { className: styles$l.actionsContainer, children: actions.map((actionProps) => /* @__PURE__ */ jsx(Button, { size: "small", ...actionProps })) })
  ] }) });
};

const useTrackCopilotSendMessageEvent = ({
  messages,
  conversationId,
  copilotId
}) => {
  const processedUserMessagesRef = useRef(/* @__PURE__ */ new Set());
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    if (!hasInitializedRef.current) {
      messages.forEach((msg) => {
        if (msg.id && msg.role === "user") {
          processedUserMessagesRef.current.add(msg.id);
        }
      });
      hasInitializedRef.current = true;
      return;
    }
    const userMessage = [...messages].reverse().find((m) => m.role === "user");
    if (!userMessage || !userMessage.id) return;
    if (!processedUserMessagesRef.current.has(userMessage.id)) {
      const commonFields = getCommonTrackingEventFields({
        conversationId,
        copilotId,
        userMessageId: userMessage.id
      });
      trackCopilotInteractionEvent({
        interaction_name: "send_message",
        interaction_goal: "send_message",
        interaction_raw_text: "Send",
        event_action: "send_message",
        subject: "message",
        ...commonFields
      });
      processedUserMessagesRef.current.add(userMessage.id);
    }
  }, [messages, conversationId, copilotId]);
};

function decodeSpanIdProjectName(spanId) {
  try {
    const decoded = atob(spanId);
    const jsonStart = decoded.indexOf('{"');
    if (jsonStart !== -1) {
      const jsonPart = decoded.slice(jsonStart);
      const parsed = JSON.parse(jsonPart);
      return parsed.compute_object_metadata_args?.project_name || null;
    }
  } catch (error) {
    console.error("Error decoding span ID:", error);
  }
  return null;
}
const CopilotContext = createContext(null);
function CopilotProvider({
  children,
  clientTools = [],
  serverToolComponents = {},
  onToolCall,
  effects = [],
  copilotId,
  registry,
  initialConversationId: providedInitialConversationId,
  initialId: providedInitialId,
  initialMessages: providedInitialMessages = [],
  generateId = generateRandomUUID,
  feedbackConfig,
  isDebug = process.env.NODE_ENV === "development",
  headers = {},
  getBody,
  getHandoffData = () => ({}),
  loadMessagesEndpoint,
  defaultCopilotMode,
  ...useChatArgs
}) {
  const [chatId, setChatId] = useState(providedInitialId ?? generateId());
  const [conversationId, setConversationId] = useState(
    providedInitialConversationId ?? generateRandomUUID()
  );
  const [copilotMode, setCopilotMode] = useState(
    defaultCopilotMode
  );
  const [spanId, setSpanId] = useState();
  const [braintrustProjectName, setBraintrustProjectName] = useState();
  const [messageSpanIds, setMessageSpanIds] = useState(
    /* @__PURE__ */ new Map()
  );
  const messageSpanIdsRef = useRef(/* @__PURE__ */ new Map());
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const isLoadingMessagesRef = useRef(false);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  if (registry && !copilotId) {
    throw new Error("copilotId is required when registry is provided");
  }
  if (registry && copilotId) {
    const copilot = registry.getById(copilotId);
    if (!copilot) {
      throw new Error(
        `Invalid copilotId "${copilotId}" - not found in registry`
      );
    }
  }
  useEffect(() => {
    if (conversationId && conversationId !== "undefined" && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      const urlConversationId = url.searchParams.get("conversationId");
      if (urlConversationId !== conversationId) {
        url.searchParams.set("conversationId", conversationId);
        window.history.replaceState({}, "", url.toString());
      }
    }
  }, [conversationId]);
  const registryOfOtherCopilots = useMemo(() => {
    if (!registry || !copilotId) return void 0;
    return createCopilotRegistry(
      registry.copilots.filter((c) => c.id !== copilotId)
    );
  }, [registry, copilotId]);
  const enhancedClientTools = useMemo(() => {
    if (registryOfOtherCopilots && copilotId) {
      const handoffTool = makeHandoffTool({
        registry: registryOfOtherCopilots,
        fromCopilotId: copilotId
      });
      return [...clientTools, handoffTool];
    } else {
      console.warn(
        "CopilotProvider: No handoff tool added because registry and copilotId are required"
      );
    }
    return clientTools;
  }, [clientTools, registryOfOtherCopilots, copilotId]);
  const toolCallHandler = useMemo(() => {
    if (onToolCall) return onToolCall;
    if (enhancedClientTools.length > 0) {
      const toolsWithExecuteFn = enhancedClientTools.filter(
        (tool) => "execute" in tool
      );
      return createOnToolCall(toolsWithExecuteFn);
    }
    return void 0;
  }, [onToolCall, enhancedClientTools]);
  const serializedClientTools = useMemo(() => {
    return serializeTools(enhancedClientTools);
  }, [enhancedClientTools]);
  const chat = useChat({
    ...useChatArgs,
    initialMessages: [],
    onToolCall: toolCallHandler,
    generateId,
    id: chatId,
    // TODO: when we upgrade to ai@5.x.x, we can use the non-experimental API to set the body via a function.
    experimental_prepareRequestBody(options) {
      return {
        clientTools: serializedClientTools,
        conversationId,
        ...options,
        ...getBody(),
        ...copilotMode ? { copilotMode } : {}
      };
    },
    headers: {
      ...headers,
      // Always send the current span ID in headers - this represents the span ID
      // that should be used for the NEXT assistant message
      ...spanId ? { [SPAN_ID_HEADERS.SPAN_ID]: spanId } : {}
    },
    onResponse: (response) => {
      const requestSpanId = spanId;
      const responseSpanId = response.headers.get(SPAN_ID_HEADERS.SPAN_ID);
      if (responseSpanId) {
        setSpanId(responseSpanId);
        if (isDebug) {
          const projectName = decodeSpanIdProjectName(responseSpanId);
          if (projectName) {
            setBraintrustProjectName(projectName);
          }
        }
      }
      if (requestSpanId) {
        setTimeout(() => {
          const messages = chat.messages;
          for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            if (msg.role === "assistant" && !messageSpanIdsRef.current.has(msg.id)) {
              const newMap = new Map(messageSpanIdsRef.current).set(
                msg.id,
                requestSpanId
              );
              messageSpanIdsRef.current = newMap;
              setMessageSpanIds(newMap);
              break;
            }
          }
        }, 100);
      }
      useChatArgs.onResponse?.(response);
    }
  });
  useHandoffStreamStopping({
    messages: chat.messages,
    status: chat.status,
    stop: chat.stop
  });
  useToolEffects({
    messages: chat.messages,
    effects,
    status: chat.status,
    conversationId,
    copilotId
  });
  useTrackCopilotSendMessageEvent({
    messages: chat.messages,
    conversationId,
    copilotId
  });
  const components = useMemo(() => {
    const toolsWithComponents = enhancedClientTools.filter(
      (tool) => "component" in tool
    );
    return {
      ...extractComponents(toolsWithComponents),
      ...serverToolComponents
    };
  }, [enhancedClientTools, serverToolComponents]);
  const resetChat = useCallback(
    ({
      newChatId = generateId(),
      newConversationId = generateRandomUUID(),
      newInitialMessages = providedInitialMessages
    } = {}) => {
      chat.stop();
      flushSync(() => {
        setChatId(newChatId);
        setConversationId(newConversationId);
        setSpanId(void 0);
        const emptyMap = /* @__PURE__ */ new Map();
        setMessageSpanIds(emptyMap);
        messageSpanIdsRef.current = emptyMap;
      });
      chat.setMessages(newInitialMessages);
      const conversationIdFromUrl = getConversationIdFromUrl();
      if (!conversationIdFromUrl) {
        return;
      }
      const newURL = new URL(window.location.href);
      newURL.searchParams.set("conversationId", newConversationId);
      window.history.replaceState({}, "", newURL.toString());
    },
    [chat.stop, chat.setMessages]
  );
  const handoff = useHandoff({
    copilotId,
    registry: registryOfOtherCopilots,
    conversationId,
    storage: defaultHandoffStorage
  });
  const stableChatSetMessages = useRef(chat.setMessages);
  const stableChatAppend = useRef(chat.append);
  useEffect(() => {
    stableChatSetMessages.current = chat.setMessages;
    stableChatAppend.current = chat.append;
  }, [chat.setMessages, chat.append]);
  const loadMessages = async (conversationId2) => {
    if (messagesLoaded || !conversationId2 || conversationId2 === "undefined" || isLoadingMessagesRef.current)
      return;
    if (!loadMessagesEndpoint) {
      if (providedInitialMessages && providedInitialMessages.length > 0) {
        chat.setMessages(providedInitialMessages);
      }
      setMessagesLoaded(true);
      return;
    }
    isLoadingMessagesRef.current = true;
    setIsLoadingMessages(true);
    try {
      const response = await fetch(
        `${loadMessagesEndpoint}/${conversationId2}/messages`
      );
      if (response.ok) {
        const data = await response.json();
        const fetchedMessages = data.messages ?? [];
        if (fetchedMessages.length > 0) {
          chat.setMessages(fetchedMessages);
        } else {
          chat.setMessages(providedInitialMessages);
        }
        setMessagesLoaded(true);
      }
    } catch (error) {
      console.warn("Failed to load conversation messages:", error);
    } finally {
      isLoadingMessagesRef.current = false;
      setIsLoadingMessages(false);
    }
  };
  useEffect(() => {
    loadMessages(conversationId);
  }, [conversationId]);
  useEffect(() => {
    if (handoff?.applyHandoff && registryOfOtherCopilots && copilotId && conversationId && messagesLoaded) {
      handoff.applyHandoff(
        providedInitialMessages,
        chat.messages,
        stableChatSetMessages.current,
        stableChatAppend.current
      );
    }
  }, [
    handoff?.applyHandoff,
    registryOfOtherCopilots,
    copilotId,
    conversationId,
    messagesLoaded
  ]);
  const finalFeedbackConfig = useMemo(() => {
    if (!feedbackConfig || !spanId) return void 0;
    return {
      ...feedbackConfig,
      spanId
    };
  }, [feedbackConfig, spanId]);
  const cancelPendingToolInvocations = useCallback(() => {
    const messages = [...chat.messages];
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === "assistant" && lastMessage.parts) {
      let hasChanges = false;
      const updatedParts = lastMessage.parts.map((part) => {
        if (part.type === "tool-invocation" && part.toolInvocation?.state === "call") {
          hasChanges = true;
          return {
            ...part,
            toolInvocation: {
              ...part.toolInvocation,
              state: "result",
              result: {
                cancelled: true,
                message: "User sent a new message before tool completed"
              }
            }
          };
        }
        return part;
      });
      if (hasChanges) {
        messages[messages.length - 1] = {
          ...lastMessage,
          parts: updatedParts
        };
        chat.setMessages(messages);
      }
    }
  }, [chat.messages, chat.setMessages]);
  const wrappedAppend = useCallback(
    (message, options) => {
      cancelPendingToolInvocations();
      return chat.append(message, options);
    },
    [chat.append, cancelPendingToolInvocations]
  );
  const wrappedHandleSubmit = useCallback(
    (event, options) => {
      cancelPendingToolInvocations();
      return chat.handleSubmit(event, options);
    },
    [chat.handleSubmit, cancelPendingToolInvocations]
  );
  const value = {
    ...chat,
    append: wrappedAppend,
    handleSubmit: wrappedHandleSubmit,
    components,
    performHandoff: handoff?.performHandoff,
    applyHandoff: handoff?.applyHandoff,
    conversationId,
    getHandoffData,
    feedbackConfig: finalFeedbackConfig,
    spanId,
    setSpanId,
    braintrustProjectName,
    messageSpanIds,
    isDebug,
    resetChat,
    isLoadingMessages,
    copilotId,
    copilotMode,
    setCopilotMode
  };
  return /* @__PURE__ */ jsx(CopilotContext.Provider, { value, children });
}
function useFeedbackConfig() {
  const context = useContext(CopilotContext);
  return context?.feedbackConfig || null;
}
function useSpanId() {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error("useSpanId must be used within a CopilotProvider");
  }
  return {
    spanId: context.spanId,
    setSpanId: context.setSpanId
  };
}
function useCopilotContext() {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error("useCopilotContext must be used within a CopilotProvider");
  }
  return context;
}

function useCopilotChat() {
  const context = useContext(CopilotContext);
  if (!context) {
    throw new Error("useCopilotChat must be used within CopilotProvider");
  }
  return context;
}
function useCopilotRegistry(getRegistry) {
  const [registry, setRegistry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const loadRegistry = async () => {
      try {
        setIsLoading(true);
        const globalRegistry = await getRegistry();
        setRegistry(globalRegistry);
        setError(null);
      } catch (err) {
        console.error("Failed to load global registry:", err);
        setError(
          err instanceof Error ? err : new Error("Failed to load global registry")
        );
      } finally {
        setIsLoading(false);
      }
    };
    loadRegistry();
  }, [getRegistry, setIsLoading, setRegistry, setError]);
  return { registry, isLoading, error };
}

const AutogrowTextarea = forwardRef((props, forwardedRef) => {
  const internalRef = useRef(null);
  useImperativeHandle(forwardedRef, () => internalRef.current);
  const resize = useCallback(() => {
    const el = internalRef.current;
    if (!el) return;
    el.style.height = "auto";
    const {
      boxSizing,
      paddingTop,
      paddingBottom,
      borderTopWidth,
      borderBottomWidth,
      maxHeight
    } = window.getComputedStyle(el);
    const toPx = (value) => parseFloat(value) || 0;
    const verticalPadding = toPx(paddingTop) + toPx(paddingBottom);
    const verticalBorder = toPx(borderTopWidth) + toPx(borderBottomWidth);
    const maxHeightPx = toPx(maxHeight);
    let targetHeight = el.scrollHeight - verticalPadding;
    if (boxSizing === "content-box") {
      targetHeight -= verticalBorder;
    }
    targetHeight = Math.max(0, targetHeight);
    if (maxHeightPx > 0 && targetHeight > maxHeightPx) {
      el.style.height = `${maxHeightPx}px`;
      el.style.overflowY = "auto";
    } else {
      el.style.height = `${targetHeight}px`;
      el.style.overflowY = "hidden";
    }
  }, []);
  useLayoutEffect(resize, [props.value, resize]);
  const handleInput = (e) => {
    resize();
    props.onInput?.(e);
  };
  useEffect(() => {
    internalRef.current?.focus();
  }, []);
  return /* @__PURE__ */ jsx(
    "textarea",
    {
      ...props,
      ref: internalRef,
      style: { ...props.style, overflow: "hidden", resize: "none" },
      onInput: handleInput
    }
  );
});

const copilotInputContainer = "_copilotInputContainer_e2xws_1";
const copilotInput = "_copilotInput_e2xws_1";
const inputRow = "_inputRow_e2xws_25";
const chipsRow = "_chipsRow_e2xws_30";
const controlsRow = "_controlsRow_e2xws_33";
const actionStartButtons = "_actionStartButtons_e2xws_40";
const submitArea = "_submitArea_e2xws_48";
const actionEndButton = "_actionEndButton_e2xws_53";
const modeToggle = "_modeToggle_e2xws_57";
const modeToggleButton = "_modeToggleButton_e2xws_65";
const textarea$2 = "_textarea_e2xws_89";
const styles$k = {
	copilotInputContainer: copilotInputContainer,
	copilotInput: copilotInput,
	inputRow: inputRow,
	chipsRow: chipsRow,
	controlsRow: controlsRow,
	actionStartButtons: actionStartButtons,
	submitArea: submitArea,
	actionEndButton: actionEndButton,
	modeToggle: modeToggle,
	modeToggleButton: modeToggleButton,
	textarea: textarea$2
};

const fileAttachment = "_fileAttachment_1sz69_1";
const fileIconContainer = "_fileIconContainer_1sz69_12";
const fileDetails = "_fileDetails_1sz69_23";
const fileHeader = "_fileHeader_1sz69_30";
const fileName = "_fileName_1sz69_37";
const fileType = "_fileType_1sz69_49";
const closeButton$1 = "_closeButton_1sz69_54";
const imageAttachment = "_imageAttachment_1sz69_59";
const imageContainer = "_imageContainer_1sz69_64";
const imageAttachmentMedium = "_imageAttachmentMedium_1sz69_75";
const imageContainerMedium = "_imageContainerMedium_1sz69_81";
const imageAttachmentLarge = "_imageAttachmentLarge_1sz69_92";
const imageContainerLarge = "_imageContainerLarge_1sz69_98";
const imageOverlay = "_imageOverlay_1sz69_107";
const closeButtonOverlay = "_closeButtonOverlay_1sz69_123";
const styles$j = {
	fileAttachment: fileAttachment,
	fileIconContainer: fileIconContainer,
	fileDetails: fileDetails,
	fileHeader: fileHeader,
	fileName: fileName,
	fileType: fileType,
	closeButton: closeButton$1,
	imageAttachment: imageAttachment,
	imageContainer: imageContainer,
	imageAttachmentMedium: imageAttachmentMedium,
	imageContainerMedium: imageContainerMedium,
	imageAttachmentLarge: imageAttachmentLarge,
	imageContainerLarge: imageContainerLarge,
	imageOverlay: imageOverlay,
	closeButtonOverlay: closeButtonOverlay
};

const lightboxOverlay = "_lightboxOverlay_1hctx_1";
const lightboxContainer = "_lightboxContainer_1hctx_15";
const lightboxImage = "_lightboxImage_1hctx_22";
const closeButton = "_closeButton_1hctx_29";
const styles$i = {
	lightboxOverlay: lightboxOverlay,
	lightboxContainer: lightboxContainer,
	lightboxImage: lightboxImage,
	closeButton: closeButton
};

const ImageLightbox = ({
  imageUrl,
  altText = "Image",
  onClose
}) => {
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: styles$i.lightboxOverlay,
      onClick: handleBackdropClick,
      role: "dialog",
      "aria-label": "Image preview",
      children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            className: styles$i.closeButton,
            variant: "ghost",
            onPress: onClose,
            "aria-label": "Close image preview",
            children: /* @__PURE__ */ jsx(Icon, { name: "formX", size: "" })
          }
        ),
        /* @__PURE__ */ jsx("div", { className: styles$i.lightboxContainer, children: /* @__PURE__ */ jsx("img", { src: imageUrl, alt: altText, className: styles$i.lightboxImage }) })
      ]
    }
  );
};

const getFileExtension$1 = (mimeType, fileName, fallbackType) => {
  if (fileName) {
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex !== -1) {
      const extension = fileName.slice(dotIndex + 1);
      if (extension) return extension.toUpperCase();
    }
  }
  const mimeMap = {
    "image/jpeg": "JPG",
    "image/png": "PNG",
    "image/gif": "GIF",
    "image/webp": "WEBP",
    "application/pdf": "PDF",
    "text/plain": "TXT",
    "text/markdown": "MD",
    "application/json": "JSON",
    "text/csv": "CSV",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX"
  };
  return mimeMap[mimeType] || fallbackType || "FILE";
};
const FileAttachment = ({
  fileName,
  fileType,
  mimeType,
  data,
  showCloseButton = false,
  onRemove,
  size = "small"
}) => {
  const isImage = mimeType.startsWith("image/");
  const displayFileType = getFileExtension$1(mimeType, fileName, fileType);
  const [showLightbox, setShowLightbox] = useState(false);
  const imageAttachmentClass = size === "large" ? styles$j.imageAttachmentLarge : size === "medium" ? styles$j.imageAttachmentMedium : styles$j.imageAttachment;
  const imageContainerClass = size === "large" ? styles$j.imageContainerLarge : size === "medium" ? styles$j.imageContainerMedium : styles$j.imageContainer;
  if (isImage && data) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("div", { className: imageAttachmentClass, children: size === "large" ? (
        // Large images use img tag to maintain aspect ratio with constrained height
        /* @__PURE__ */ jsx(
          "img",
          {
            src: data,
            alt: fileName,
            title: fileName,
            className: imageContainerClass,
            onClick: () => setShowLightbox(true),
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowLightbox(true);
              }
            },
            role: "button",
            tabIndex: 0,
            "aria-label": "Click to view image in full size"
          }
        )
      ) : size === "medium" ? (
        // Medium images use background image for fixed dimensions in message history
        /* @__PURE__ */ jsx(
          "div",
          {
            className: imageContainerClass,
            style: { backgroundImage: `url('${data}')` },
            title: fileName,
            onClick: () => setShowLightbox(true),
            role: "button",
            tabIndex: 0,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowLightbox(true);
              }
            },
            "aria-label": "Click to view image in full size"
          }
        )
      ) : (
        // Small images use background image for fixed square dimensions
        /* @__PURE__ */ jsx(
          "div",
          {
            className: imageContainerClass,
            style: { backgroundImage: `url('${data}')` },
            title: fileName,
            onClick: () => setShowLightbox(true),
            role: "button",
            tabIndex: 0,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowLightbox(true);
              }
            },
            "aria-label": "Click to view image in full size",
            children: showCloseButton && onRemove && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("div", { className: styles$j.imageOverlay }),
              /* @__PURE__ */ jsx("div", { className: styles$j.closeButtonOverlay, children: /* @__PURE__ */ jsx(Tooltip, { content: "Remove file", children: /* @__PURE__ */ jsx(
                "div",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    onRemove();
                  },
                  role: "button",
                  tabIndex: 0,
                  "aria-label": "Remove file",
                  onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemove();
                    }
                  },
                  children: /* @__PURE__ */ jsx(Icon, { name: "formX", size: "xsmall", color: "TextDefault" })
                }
              ) }) })
            ] })
          }
        )
      ) }),
      showLightbox && /* @__PURE__ */ jsx(
        ImageLightbox,
        {
          imageUrl: data,
          altText: fileName,
          onClose: () => setShowLightbox(false)
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: styles$j.fileAttachment, children: [
    /* @__PURE__ */ jsx("div", { className: styles$j.fileIconContainer, children: /* @__PURE__ */ jsx(Icon, { name: "fileFilled", size: "small", color: "TextInverted" }) }),
    /* @__PURE__ */ jsxs("div", { className: styles$j.fileDetails, children: [
      /* @__PURE__ */ jsxs("div", { className: styles$j.fileHeader, children: [
        /* @__PURE__ */ jsx("span", { className: styles$j.fileName, title: fileName, children: fileName }),
        showCloseButton && onRemove && /* @__PURE__ */ jsx("div", { className: styles$j.closeButton, children: /* @__PURE__ */ jsx(Tooltip, { content: "Remove file", children: /* @__PURE__ */ jsx(
          IconButton,
          {
            name: "formX",
            size: "small",
            variant: "ghost",
            onPress: onRemove,
            "aria-label": "Remove file"
          }
        ) }) })
      ] }),
      /* @__PURE__ */ jsx("div", { className: styles$j.fileType, children: displayFileType })
    ] })
  ] });
};

const fileUploadContainer = "_fileUploadContainer_1vti7_1";
const filesContainer = "_filesContainer_1vti7_8";
const attachmentControls = "_attachmentControls_1vti7_16";
const errorMessage$1 = "_errorMessage_1vti7_22";
const styles$h = {
	fileUploadContainer: fileUploadContainer,
	filesContainer: filesContainer,
	attachmentControls: attachmentControls,
	errorMessage: errorMessage$1};

const useFileUploadState = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const handleFileSelect = useCallback((file) => {
    setError(null);
    setSelectedFiles((prev) => [...prev, file]);
  }, []);
  const removeFile = useCallback((index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);
  const clearFiles = useCallback(() => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);
  return {
    selectedFiles,
    handleFileSelect,
    removeFile,
    clearFiles,
    fileInputRef,
    error,
    setError
  };
};
const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
const documentExtensions = [".pdf", ".txt", ".md", ".csv"];
const codeExtensions = [".c", ".cpp", ".html", ".java", ".php", ".py"];
const supportedExtensions = [
  ...imageExtensions,
  ...documentExtensions,
  ...codeExtensions
];
const getSupportedFileTypesDescription = () => {
  const imageTypes = imageExtensions.map((ext) => ext.slice(1).toUpperCase()).join(", ");
  const docTypes = documentExtensions.map((ext) => ext.slice(1).toUpperCase()).join(", ");
  return `images (${imageTypes}), documents (${docTypes}), or code files`;
};
const getUnsupportedFileTypeError = (extension) => {
  const fileType = extension.toUpperCase() || "This file type";
  return `${fileType} files are not supported. Please upload ${getSupportedFileTypesDescription()}.`;
};
const getPrettyFileSize = (bytes) => {
  return `${(bytes / 1e6).toFixed(1)} MB`;
};
const getFileName = (filePath) => filePath.split("\\").pop()?.split("/").pop() || "file";
const getFileExtension = (filePath) => {
  const fileName = getFileName(filePath);
  const extension = fileName.slice(Math.max(0, fileName.lastIndexOf(".") + 1));
  return extension.toUpperCase() || "FILE";
};
const FileListDisplay = ({
  selectedFiles,
  removeFile,
  error,
  className
}) => {
  if (selectedFiles.length === 0 && !error) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: clsx$1(styles$h.fileUploadContainer, className), children: [
    selectedFiles.length > 0 && /* @__PURE__ */ jsx("div", { className: styles$h.filesContainer, children: selectedFiles.map((fileData, index) => /* @__PURE__ */ jsx(
      FileAttachment,
      {
        fileName: fileData.name,
        fileType: getFileExtension(fileData.name),
        mimeType: fileData.type,
        data: fileData.data,
        showCloseButton: true,
        onRemove: () => removeFile(index)
      },
      index
    )) }),
    error && /* @__PURE__ */ jsxs("div", { className: styles$h.errorMessage, children: [
      /* @__PURE__ */ jsx(Icon, { name: "formXCircleOutlined", size: "small" }),
      error
    ] })
  ] });
};
const FileUploadButton = ({
  handleFileSelect,
  setError,
  isDisabled = false,
  containerRef
}) => {
  const processFiles = useCallback(
    (files) => {
      files.forEach((file) => {
        const reader = new FileReader();
        const fileName = file.name;
        const extension = fileName.slice(
          Math.max(0, fileName.lastIndexOf("."))
        );
        const supportedFileTypes = new Set(supportedExtensions);
        if (!supportedFileTypes.has(extension)) {
          setError(getUnsupportedFileTypeError(extension));
          return;
        }
        const maxFileSize = 4e6;
        if (file.size > maxFileSize) {
          const prettyFileSize = getPrettyFileSize(maxFileSize);
          setError(`File ${fileName} exceeds ${prettyFileSize}`);
          return;
        }
        reader.onloadend = () => {
          const base64String = reader.result;
          const fileData = {
            data: base64String,
            name: fileName,
            type: file.type
          };
          handleFileSelect(fileData);
        };
        reader.readAsDataURL(file);
      });
    },
    [handleFileSelect, setError]
  );
  const handlePaste = useCallback(
    (e) => {
      if (isDisabled) return;
      if (containerRef?.current) {
        const activeElement = document.activeElement;
        const containerElement = containerRef.current;
        if (!containerElement.contains(activeElement)) {
          return;
        }
      }
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;
      const files = Array.from(clipboardData.files);
      if (files.length === 0) return;
      e.preventDefault();
      processFiles(files);
    },
    [isDisabled, processFiles, containerRef]
  );
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDisabled) return;
      const dragEvent = e;
      const files = Array.from(dragEvent.dataTransfer?.files || []);
      if (files.length > 0) {
        processFiles(files);
      }
    },
    [isDisabled, processFiles]
  );
  useEffect(() => {
    const targetElement = containerRef?.current || document;
    document.addEventListener("paste", handlePaste);
    targetElement.addEventListener("dragenter", handleDragEnter);
    targetElement.addEventListener("dragover", handleDragOver);
    targetElement.addEventListener("dragleave", handleDragLeave);
    targetElement.addEventListener("drop", handleDrop);
    return () => {
      document.removeEventListener("paste", handlePaste);
      targetElement.removeEventListener("dragenter", handleDragEnter);
      targetElement.removeEventListener("dragover", handleDragOver);
      targetElement.removeEventListener("dragleave", handleDragLeave);
      targetElement.removeEventListener("drop", handleDrop);
    };
  }, [
    handlePaste,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    containerRef
  ]);
  const handleFileTrigger = useCallback(
    (files) => {
      if (!files || files.length === 0) return;
      processFiles(Array.from(files));
    },
    [processFiles]
  );
  return /* @__PURE__ */ jsx("div", { className: styles$h.attachmentControls, children: /* @__PURE__ */ jsx(
    FileTrigger,
    {
      acceptedFileTypes: supportedExtensions,
      onSelect: handleFileTrigger,
      allowsMultiple: true,
      children: /* @__PURE__ */ jsx(Tooltip, { content: "Attach image or file", children: /* @__PURE__ */ jsx(
        IconButton,
        {
          name: "formAdd",
          size: "small",
          variant: "ghost",
          isDisabled,
          "aria-label": "Attach file"
        }
      ) })
    }
  ) });
};
const FileUploadControls = ({
  selectedFiles,
  handleFileSelect,
  removeFile,
  error,
  setError,
  className,
  isDisabled = false,
  containerRef
}) => {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      FileListDisplay,
      {
        selectedFiles,
        removeFile,
        error,
        className
      }
    ),
    /* @__PURE__ */ jsx(
      FileUploadButton,
      {
        handleFileSelect,
        setError,
        isDisabled,
        containerRef
      }
    )
  ] });
};

const ModeToggle = ({
  className,
  defaultSelectedMode = CopilotMode.autoBuild,
  isDisabled = false,
  selectedMode,
  style,
  onChange: onModeChange
}) => {
  const handleSelectionChange = (keys) => {
    const key = Array.from(keys)[0];
    onModeChange?.(key);
  };
  const selectedKey = selectedMode ? selectedMode : void 0;
  const defaultKey = !selectedMode ? defaultSelectedMode : void 0;
  return /* @__PURE__ */ jsxs(
    ToggleButtonGroup,
    {
      "aria-label": "Set auto-build mode",
      className: clsx$1(styles$k.modeToggle, className),
      style,
      disallowEmptySelection: true,
      isDisabled,
      selectedKeys: selectedKey ? [selectedKey] : void 0,
      defaultSelectedKeys: defaultKey ? [defaultKey] : void 0,
      onSelectionChange: handleSelectionChange,
      children: [
        /* @__PURE__ */ jsx(
          ToggleButton,
          {
            className: styles$k.modeToggleButton,
            id: CopilotMode.autoBuild,
            children: "Auto"
          }
        ),
        /* @__PURE__ */ jsx(
          ToggleButton,
          {
            className: styles$k.modeToggleButton,
            id: CopilotMode.askAsYouBuild,
            children: "Ask"
          }
        )
      ]
    }
  );
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

/** Detect free variable `global` from Node.js. */

var _freeGlobal;
var hasRequired_freeGlobal;

function require_freeGlobal () {
	if (hasRequired_freeGlobal) return _freeGlobal;
	hasRequired_freeGlobal = 1;
	var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

	_freeGlobal = freeGlobal;
	return _freeGlobal;
}

var _root;
var hasRequired_root;

function require_root () {
	if (hasRequired_root) return _root;
	hasRequired_root = 1;
	var freeGlobal = require_freeGlobal();

	/** Detect free variable `self`. */
	var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

	/** Used as a reference to the global object. */
	var root = freeGlobal || freeSelf || Function('return this')();

	_root = root;
	return _root;
}

var _Symbol;
var hasRequired_Symbol;

function require_Symbol () {
	if (hasRequired_Symbol) return _Symbol;
	hasRequired_Symbol = 1;
	var root = require_root();

	/** Built-in value references. */
	var Symbol = root.Symbol;

	_Symbol = Symbol;
	return _Symbol;
}

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */

var _arrayMap;
var hasRequired_arrayMap;

function require_arrayMap () {
	if (hasRequired_arrayMap) return _arrayMap;
	hasRequired_arrayMap = 1;
	function arrayMap(array, iteratee) {
	  var index = -1,
	      length = array == null ? 0 : array.length,
	      result = Array(length);

	  while (++index < length) {
	    result[index] = iteratee(array[index], index, array);
	  }
	  return result;
	}

	_arrayMap = arrayMap;
	return _arrayMap;
}

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */

var isArray_1;
var hasRequiredIsArray;

function requireIsArray () {
	if (hasRequiredIsArray) return isArray_1;
	hasRequiredIsArray = 1;
	var isArray = Array.isArray;

	isArray_1 = isArray;
	return isArray_1;
}

var _getRawTag;
var hasRequired_getRawTag;

function require_getRawTag () {
	if (hasRequired_getRawTag) return _getRawTag;
	hasRequired_getRawTag = 1;
	var Symbol = require_Symbol();

	/** Used for built-in method references. */
	var objectProto = Object.prototype;

	/** Used to check objects for own properties. */
	var hasOwnProperty = objectProto.hasOwnProperty;

	/**
	 * Used to resolve the
	 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
	 * of values.
	 */
	var nativeObjectToString = objectProto.toString;

	/** Built-in value references. */
	var symToStringTag = Symbol ? Symbol.toStringTag : undefined;

	/**
	 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
	 *
	 * @private
	 * @param {*} value The value to query.
	 * @returns {string} Returns the raw `toStringTag`.
	 */
	function getRawTag(value) {
	  var isOwn = hasOwnProperty.call(value, symToStringTag),
	      tag = value[symToStringTag];

	  try {
	    value[symToStringTag] = undefined;
	    var unmasked = true;
	  } catch (e) {}

	  var result = nativeObjectToString.call(value);
	  if (unmasked) {
	    if (isOwn) {
	      value[symToStringTag] = tag;
	    } else {
	      delete value[symToStringTag];
	    }
	  }
	  return result;
	}

	_getRawTag = getRawTag;
	return _getRawTag;
}

/** Used for built-in method references. */

var _objectToString;
var hasRequired_objectToString;

function require_objectToString () {
	if (hasRequired_objectToString) return _objectToString;
	hasRequired_objectToString = 1;
	var objectProto = Object.prototype;

	/**
	 * Used to resolve the
	 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
	 * of values.
	 */
	var nativeObjectToString = objectProto.toString;

	/**
	 * Converts `value` to a string using `Object.prototype.toString`.
	 *
	 * @private
	 * @param {*} value The value to convert.
	 * @returns {string} Returns the converted string.
	 */
	function objectToString(value) {
	  return nativeObjectToString.call(value);
	}

	_objectToString = objectToString;
	return _objectToString;
}

var _baseGetTag;
var hasRequired_baseGetTag;

function require_baseGetTag () {
	if (hasRequired_baseGetTag) return _baseGetTag;
	hasRequired_baseGetTag = 1;
	var Symbol = require_Symbol(),
	    getRawTag = require_getRawTag(),
	    objectToString = require_objectToString();

	/** `Object#toString` result references. */
	var nullTag = '[object Null]',
	    undefinedTag = '[object Undefined]';

	/** Built-in value references. */
	var symToStringTag = Symbol ? Symbol.toStringTag : undefined;

	/**
	 * The base implementation of `getTag` without fallbacks for buggy environments.
	 *
	 * @private
	 * @param {*} value The value to query.
	 * @returns {string} Returns the `toStringTag`.
	 */
	function baseGetTag(value) {
	  if (value == null) {
	    return value === undefined ? undefinedTag : nullTag;
	  }
	  return (symToStringTag && symToStringTag in Object(value))
	    ? getRawTag(value)
	    : objectToString(value);
	}

	_baseGetTag = baseGetTag;
	return _baseGetTag;
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

var isObjectLike_1;
var hasRequiredIsObjectLike;

function requireIsObjectLike () {
	if (hasRequiredIsObjectLike) return isObjectLike_1;
	hasRequiredIsObjectLike = 1;
	function isObjectLike(value) {
	  return value != null && typeof value == 'object';
	}

	isObjectLike_1 = isObjectLike;
	return isObjectLike_1;
}

var isSymbol_1;
var hasRequiredIsSymbol;

function requireIsSymbol () {
	if (hasRequiredIsSymbol) return isSymbol_1;
	hasRequiredIsSymbol = 1;
	var baseGetTag = require_baseGetTag(),
	    isObjectLike = requireIsObjectLike();

	/** `Object#toString` result references. */
	var symbolTag = '[object Symbol]';

	/**
	 * Checks if `value` is classified as a `Symbol` primitive or object.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.0.0
	 * @category Lang
	 * @param {*} value The value to check.
	 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
	 * @example
	 *
	 * _.isSymbol(Symbol.iterator);
	 * // => true
	 *
	 * _.isSymbol('abc');
	 * // => false
	 */
	function isSymbol(value) {
	  return typeof value == 'symbol' ||
	    (isObjectLike(value) && baseGetTag(value) == symbolTag);
	}

	isSymbol_1 = isSymbol;
	return isSymbol_1;
}

var _baseToString;
var hasRequired_baseToString;

function require_baseToString () {
	if (hasRequired_baseToString) return _baseToString;
	hasRequired_baseToString = 1;
	var Symbol = require_Symbol(),
	    arrayMap = require_arrayMap(),
	    isArray = requireIsArray(),
	    isSymbol = requireIsSymbol();

	/** Used to convert symbols to primitives and strings. */
	var symbolProto = Symbol ? Symbol.prototype : undefined,
	    symbolToString = symbolProto ? symbolProto.toString : undefined;

	/**
	 * The base implementation of `_.toString` which doesn't convert nullish
	 * values to empty strings.
	 *
	 * @private
	 * @param {*} value The value to process.
	 * @returns {string} Returns the string.
	 */
	function baseToString(value) {
	  // Exit early for strings to avoid a performance hit in some environments.
	  if (typeof value == 'string') {
	    return value;
	  }
	  if (isArray(value)) {
	    // Recursively convert values (susceptible to call stack limits).
	    return arrayMap(value, baseToString) + '';
	  }
	  if (isSymbol(value)) {
	    return symbolToString ? symbolToString.call(value) : '';
	  }
	  var result = (value + '');
	  return (result == '0' && (1 / value) == -Infinity) ? '-0' : result;
	}

	_baseToString = baseToString;
	return _baseToString;
}

var toString_1;
var hasRequiredToString;

function requireToString () {
	if (hasRequiredToString) return toString_1;
	hasRequiredToString = 1;
	var baseToString = require_baseToString();

	/**
	 * Converts `value` to a string. An empty string is returned for `null`
	 * and `undefined` values. The sign of `-0` is preserved.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.0.0
	 * @category Lang
	 * @param {*} value The value to convert.
	 * @returns {string} Returns the converted string.
	 * @example
	 *
	 * _.toString(null);
	 * // => ''
	 *
	 * _.toString(-0);
	 * // => '-0'
	 *
	 * _.toString([1, 2, 3]);
	 * // => '1,2,3'
	 */
	function toString(value) {
	  return value == null ? '' : baseToString(value);
	}

	toString_1 = toString;
	return toString_1;
}

/**
 * The base implementation of `_.slice` without an iteratee call guard.
 *
 * @private
 * @param {Array} array The array to slice.
 * @param {number} [start=0] The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the slice of `array`.
 */

var _baseSlice;
var hasRequired_baseSlice;

function require_baseSlice () {
	if (hasRequired_baseSlice) return _baseSlice;
	hasRequired_baseSlice = 1;
	function baseSlice(array, start, end) {
	  var index = -1,
	      length = array.length;

	  if (start < 0) {
	    start = -start > length ? 0 : (length + start);
	  }
	  end = end > length ? length : end;
	  if (end < 0) {
	    end += length;
	  }
	  length = start > end ? 0 : ((end - start) >>> 0);
	  start >>>= 0;

	  var result = Array(length);
	  while (++index < length) {
	    result[index] = array[index + start];
	  }
	  return result;
	}

	_baseSlice = baseSlice;
	return _baseSlice;
}

var _castSlice;
var hasRequired_castSlice;

function require_castSlice () {
	if (hasRequired_castSlice) return _castSlice;
	hasRequired_castSlice = 1;
	var baseSlice = require_baseSlice();

	/**
	 * Casts `array` to a slice if it's needed.
	 *
	 * @private
	 * @param {Array} array The array to inspect.
	 * @param {number} start The start position.
	 * @param {number} [end=array.length] The end position.
	 * @returns {Array} Returns the cast slice.
	 */
	function castSlice(array, start, end) {
	  var length = array.length;
	  end = end === undefined ? length : end;
	  return (!start && end >= length) ? array : baseSlice(array, start, end);
	}

	_castSlice = castSlice;
	return _castSlice;
}

/** Used to compose unicode character classes. */

var _hasUnicode;
var hasRequired_hasUnicode;

function require_hasUnicode () {
	if (hasRequired_hasUnicode) return _hasUnicode;
	hasRequired_hasUnicode = 1;
	var rsAstralRange = '\\ud800-\\udfff',
	    rsComboMarksRange = '\\u0300-\\u036f',
	    reComboHalfMarksRange = '\\ufe20-\\ufe2f',
	    rsComboSymbolsRange = '\\u20d0-\\u20ff',
	    rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
	    rsVarRange = '\\ufe0e\\ufe0f';

	/** Used to compose unicode capture groups. */
	var rsZWJ = '\\u200d';

	/** Used to detect strings with [zero-width joiners or code points from the astral planes](http://eev.ee/blog/2015/09/12/dark-corners-of-unicode/). */
	var reHasUnicode = RegExp('[' + rsZWJ + rsAstralRange  + rsComboRange + rsVarRange + ']');

	/**
	 * Checks if `string` contains Unicode symbols.
	 *
	 * @private
	 * @param {string} string The string to inspect.
	 * @returns {boolean} Returns `true` if a symbol is found, else `false`.
	 */
	function hasUnicode(string) {
	  return reHasUnicode.test(string);
	}

	_hasUnicode = hasUnicode;
	return _hasUnicode;
}

/**
 * Converts an ASCII `string` to an array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the converted array.
 */

var _asciiToArray;
var hasRequired_asciiToArray;

function require_asciiToArray () {
	if (hasRequired_asciiToArray) return _asciiToArray;
	hasRequired_asciiToArray = 1;
	function asciiToArray(string) {
	  return string.split('');
	}

	_asciiToArray = asciiToArray;
	return _asciiToArray;
}

/** Used to compose unicode character classes. */

var _unicodeToArray;
var hasRequired_unicodeToArray;

function require_unicodeToArray () {
	if (hasRequired_unicodeToArray) return _unicodeToArray;
	hasRequired_unicodeToArray = 1;
	var rsAstralRange = '\\ud800-\\udfff',
	    rsComboMarksRange = '\\u0300-\\u036f',
	    reComboHalfMarksRange = '\\ufe20-\\ufe2f',
	    rsComboSymbolsRange = '\\u20d0-\\u20ff',
	    rsComboRange = rsComboMarksRange + reComboHalfMarksRange + rsComboSymbolsRange,
	    rsVarRange = '\\ufe0e\\ufe0f';

	/** Used to compose unicode capture groups. */
	var rsAstral = '[' + rsAstralRange + ']',
	    rsCombo = '[' + rsComboRange + ']',
	    rsFitz = '\\ud83c[\\udffb-\\udfff]',
	    rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
	    rsNonAstral = '[^' + rsAstralRange + ']',
	    rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
	    rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
	    rsZWJ = '\\u200d';

	/** Used to compose unicode regexes. */
	var reOptMod = rsModifier + '?',
	    rsOptVar = '[' + rsVarRange + ']?',
	    rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
	    rsSeq = rsOptVar + reOptMod + rsOptJoin,
	    rsSymbol = '(?:' + [rsNonAstral + rsCombo + '?', rsCombo, rsRegional, rsSurrPair, rsAstral].join('|') + ')';

	/** Used to match [string symbols](https://mathiasbynens.be/notes/javascript-unicode). */
	var reUnicode = RegExp(rsFitz + '(?=' + rsFitz + ')|' + rsSymbol + rsSeq, 'g');

	/**
	 * Converts a Unicode `string` to an array.
	 *
	 * @private
	 * @param {string} string The string to convert.
	 * @returns {Array} Returns the converted array.
	 */
	function unicodeToArray(string) {
	  return string.match(reUnicode) || [];
	}

	_unicodeToArray = unicodeToArray;
	return _unicodeToArray;
}

var _stringToArray;
var hasRequired_stringToArray;

function require_stringToArray () {
	if (hasRequired_stringToArray) return _stringToArray;
	hasRequired_stringToArray = 1;
	var asciiToArray = require_asciiToArray(),
	    hasUnicode = require_hasUnicode(),
	    unicodeToArray = require_unicodeToArray();

	/**
	 * Converts `string` to an array.
	 *
	 * @private
	 * @param {string} string The string to convert.
	 * @returns {Array} Returns the converted array.
	 */
	function stringToArray(string) {
	  return hasUnicode(string)
	    ? unicodeToArray(string)
	    : asciiToArray(string);
	}

	_stringToArray = stringToArray;
	return _stringToArray;
}

var _createCaseFirst;
var hasRequired_createCaseFirst;

function require_createCaseFirst () {
	if (hasRequired_createCaseFirst) return _createCaseFirst;
	hasRequired_createCaseFirst = 1;
	var castSlice = require_castSlice(),
	    hasUnicode = require_hasUnicode(),
	    stringToArray = require_stringToArray(),
	    toString = requireToString();

	/**
	 * Creates a function like `_.lowerFirst`.
	 *
	 * @private
	 * @param {string} methodName The name of the `String` case method to use.
	 * @returns {Function} Returns the new case function.
	 */
	function createCaseFirst(methodName) {
	  return function(string) {
	    string = toString(string);

	    var strSymbols = hasUnicode(string)
	      ? stringToArray(string)
	      : undefined;

	    var chr = strSymbols
	      ? strSymbols[0]
	      : string.charAt(0);

	    var trailing = strSymbols
	      ? castSlice(strSymbols, 1).join('')
	      : string.slice(1);

	    return chr[methodName]() + trailing;
	  };
	}

	_createCaseFirst = createCaseFirst;
	return _createCaseFirst;
}

var upperFirst_1;
var hasRequiredUpperFirst;

function requireUpperFirst () {
	if (hasRequiredUpperFirst) return upperFirst_1;
	hasRequiredUpperFirst = 1;
	var createCaseFirst = require_createCaseFirst();

	/**
	 * Converts the first character of `string` to upper case.
	 *
	 * @static
	 * @memberOf _
	 * @since 4.0.0
	 * @category String
	 * @param {string} [string=''] The string to convert.
	 * @returns {string} Returns the converted string.
	 * @example
	 *
	 * _.upperFirst('fred');
	 * // => 'Fred'
	 *
	 * _.upperFirst('FRED');
	 * // => 'FRED'
	 */
	var upperFirst = createCaseFirst('toUpperCase');

	upperFirst_1 = upperFirst;
	return upperFirst_1;
}

var capitalize_1;
var hasRequiredCapitalize;

function requireCapitalize () {
	if (hasRequiredCapitalize) return capitalize_1;
	hasRequiredCapitalize = 1;
	var toString = requireToString(),
	    upperFirst = requireUpperFirst();

	/**
	 * Converts the first character of `string` to upper case and the remaining
	 * to lower case.
	 *
	 * @static
	 * @memberOf _
	 * @since 3.0.0
	 * @category String
	 * @param {string} [string=''] The string to capitalize.
	 * @returns {string} Returns the capitalized string.
	 * @example
	 *
	 * _.capitalize('FRED');
	 * // => 'Fred'
	 */
	function capitalize(string) {
	  return upperFirst(toString(string).toLowerCase());
	}

	capitalize_1 = capitalize;
	return capitalize_1;
}

var capitalizeExports = requireCapitalize();
const capitalize = /*@__PURE__*/getDefaultExportFromCjs(capitalizeExports);

const SpeechToText = ({
  isDisabled = false,
  onTranscriptChange,
  textareaRef
}) => {
  const { isMicrophoneAvailable, listening, resetTranscript, transcript } = useSpeechRecognition();
  const handlePress = useCallback(async () => {
    if (listening) {
      setTimeout(async () => {
        await SpeechRecognition.stopListening();
      }, 500);
    } else if (isMicrophoneAvailable) {
      resetTranscript();
      setTimeout(async () => {
        await SpeechRecognition.startListening({
          continuous: true,
          language: "en-US"
        });
      }, 100);
    }
  }, [isMicrophoneAvailable, listening, resetTranscript]);
  useEffect(() => {
    if (!listening && transcript) {
      const capitalizedTranscript = capitalize(transcript);
      onTranscriptChange(capitalizedTranscript);
      textareaRef?.current?.focus();
    }
  }, [listening, onTranscriptChange, textareaRef, transcript]);
  return /* @__PURE__ */ jsx(Tooltip, { content: listening ? "Stop dictating" : "Dictate", children: /* @__PURE__ */ jsx(
    IconButton,
    {
      isDisabled,
      isSelected: listening,
      name: listening ? "statusStop" : "miscMic",
      onPress: handlePress,
      size: "small",
      variant: "ghost"
    }
  ) });
};

function convertFileUploadStateToFileList(fileUploadState) {
  if (!fileUploadState?.selectedFiles.length) {
    return void 0;
  }
  const dataTransfer = new DataTransfer();
  fileUploadState.selectedFiles.forEach((fileData) => {
    const base64String = fileData.data.split(",")[1];
    const mime = fileData.type;
    const byteCharacters = atob(base64String);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = [];
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    const blob = new Blob(byteArrays, { type: mime });
    const file = new File([blob], fileData.name, { type: mime });
    dataTransfer.items.add(file);
  });
  return dataTransfer.files;
}
function handleSubmitWithFileAttachments(fileUploadState, handleSubmit, clearFilesAfterSubmit = true) {
  const fileList = convertFileUploadStateToFileList(fileUploadState);
  try {
    handleSubmit(void 0, {
      experimental_attachments: fileList
    });
  } finally {
    if (clearFilesAfterSubmit && fileUploadState?.clearFiles) {
      fileUploadState.clearFiles();
    }
  }
}

const CopilotInput = forwardRef(
  ({
    actions,
    chips,
    className,
    handleInputChange,
    handleSubmit,
    input,
    isDisabled = false,
    placeholder,
    setInput,
    status = "ready",
    stop,
    style,
    jsonModeState = [false, () => {
    }],
    isDebug = false,
    dragDropContainerRef
  }, ref) => {
    const [isJsonMode, setIsJsonMode] = jsonModeState;
    const textareaRef = useRef(null);
    const { copilotMode, setCopilotMode } = useCopilotChat();
    const containerRef = useRef(null);
    const fileUploadState = useFileUploadState();
    useImperativeHandle(
      ref,
      () => textareaRef.current
    );
    useEffect(() => {
      if (actions) {
        console.warn(
          "actions prop is deprecated. CopilotInput now internally determines which actions are displayed."
        );
      }
    }, [actions]);
    const isLoading = status === "submitted" || status === "streaming";
    const handleChange = useCallback(
      (e) => {
        handleInputChange(e);
      },
      [handleInputChange]
    );
    const onSubmit = useCallback(
      (e) => {
        if (e) e.preventDefault();
        if (input.trim() && !isLoading) {
          if (fileUploadState.selectedFiles.length > 0) {
            const fileList = convertFileUploadStateToFileList(fileUploadState);
            handleSubmit(void 0, { experimental_attachments: fileList });
            fileUploadState.clearFiles();
          } else {
            handleSubmit();
          }
        }
      },
      [input, isLoading, handleSubmit, fileUploadState]
    );
    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onSubmit();
        }
      },
      [onSubmit]
    );
    const focusInput = useCallback(() => {
      textareaRef.current?.focus();
    }, []);
    const onRemove = useCallback(
      (ids) => {
        Array.from(ids).forEach((id) => {
          const chip = chips?.find((c) => c.id === id);
          chip?.onRemove?.(id);
        });
      },
      [chips]
    );
    const submitArea = useMemo(() => {
      if (isLoading) {
        return /* @__PURE__ */ jsxs(
          Button,
          {
            type: "button",
            variant: "danger",
            iconPosition: "start",
            size: "compact",
            onPress: stop,
            children: [
              /* @__PURE__ */ jsx(Icon, { name: "statusStopFill" }),
              "Stop"
            ]
          }
        );
      }
      return /* @__PURE__ */ jsx(Tooltip, { content: "Send", children: /* @__PURE__ */ jsx(
        IconButton,
        {
          name: "arrowBigUp",
          variant: "primary",
          size: "small",
          isDisabled: !input.trim(),
          onPress: () => onSubmit()
        }
      ) });
    }, [isLoading, stop, input, onSubmit]);
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: clsx$1(styles$k.copilotInputContainer, className),
        "data-disabled": isDisabled ? "true" : void 0,
        style,
        children: /* @__PURE__ */ jsxs(
          "form",
          {
            ref: containerRef,
            className: styles$k.copilotInput,
            onClick: focusInput,
            onSubmit,
            children: [
              /* @__PURE__ */ jsx("div", { className: styles$k.inputRow, children: /* @__PURE__ */ jsx(
                AutogrowTextarea,
                {
                  className: styles$k.textarea,
                  ref: textareaRef,
                  value: input,
                  placeholder: placeholder || "Chat with Copilot",
                  rows: 1,
                  disabled: isDisabled,
                  onChange: handleChange,
                  onKeyDown: handleKeyDown
                }
              ) }),
              chips && chips.length > 0 && /* @__PURE__ */ jsx("div", { className: styles$k.chipsRow, children: /* @__PURE__ */ jsx(
                FilterChipList,
                {
                  "aria-label": "Filter options",
                  variant: "primary",
                  size: "compact",
                  items: chips,
                  onRemove,
                  children: (item) => {
                    const originalChip = chips?.find(
                      (chip) => chip.id === item.id
                    );
                    const icon = originalChip?.icon;
                    return /* @__PURE__ */ jsx(FilterChip, { id: item.id, title: item.title, children: icon && (typeof icon === "string" ? /* @__PURE__ */ jsx(FilterChipIcon, { name: icon }) : icon) });
                  }
                }
              ) }),
              /* @__PURE__ */ jsx(
                FileListDisplay,
                {
                  selectedFiles: fileUploadState.selectedFiles,
                  removeFile: fileUploadState.removeFile,
                  error: fileUploadState.error
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: styles$k.controlsRow, children: [
                /* @__PURE__ */ jsxs("div", { className: styles$k.actionStartButtons, children: [
                  /* @__PURE__ */ jsx(
                    FileUploadButton,
                    {
                      handleFileSelect: fileUploadState.handleFileSelect,
                      setError: fileUploadState.setError,
                      isDisabled,
                      containerRef: dragDropContainerRef || containerRef
                    }
                  ),
                  copilotMode !== void 0 && /* @__PURE__ */ jsx(
                    ModeToggle,
                    {
                      isDisabled,
                      selectedMode: copilotMode,
                      onChange: (mode) => {
                        setCopilotMode(mode);
                      }
                    }
                  )
                ] }),
                /* @__PURE__ */ jsxs("div", { className: styles$k.submitArea, children: [
                  isDebug && /* @__PURE__ */ jsx(Tooltip, { content: isJsonMode ? "View Messages" : "View JSON", children: /* @__PURE__ */ jsx(
                    Button,
                    {
                      className: styles$k.actionEndButton,
                      type: "button",
                      variant: isJsonMode ? "secondary" : "tertiary",
                      size: "small",
                      onPress: () => setIsJsonMode(!isJsonMode),
                      isDisabled: isLoading,
                      "aria-label": isJsonMode ? "View Messages" : "View JSON",
                      "data-zds": true,
                      children: /* @__PURE__ */ jsx(
                        Icon,
                        {
                          name: isJsonMode ? "commentConvo" : "actionBug",
                          size: "small"
                        }
                      )
                    }
                  ) }),
                  /* @__PURE__ */ jsx(
                    SpeechToText,
                    {
                      isDisabled,
                      onTranscriptChange: setInput || (() => {
                      }),
                      textareaRef
                    }
                  ),
                  submitArea
                ] })
              ] })
            ]
          }
        )
      }
    );
  }
);

const CopilotInterruptionAlert = ({
  visible,
  onRetry,
  onDismiss: _onDismiss,
  title = "Copilot got interrupted",
  description = "You haven't lost progress. Try again to continue.",
  retryText = "Retry",
  className
}) => {
  const { reload } = useCopilotChat();
  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
    } else {
      reload();
    }
  }, [onRetry, reload]);
  if (!visible) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    CopilotAlert,
    {
      status: "warning",
      className,
      title,
      message: description,
      actions: [
        {
          variant: "primary",
          size: "small",
          iconPosition: "start",
          children: /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(Icon$1, { name: "arrowRefresh" }),
            retryText
          ] }),
          onPress: handleRetry
        }
      ]
    }
  );
};

const dialog = "_dialog_1injp_1";
const modal = "_modal_1injp_17";
const header$2 = "_header_1injp_25";
const body$1 = "_body_1injp_32";
const bodyContent = "_bodyContent_1injp_37";
const labelSection = "_labelSection_1injp_43";
const textarea$1 = "_textarea_1injp_47";
const footer$2 = "_footer_1injp_67";
const labelText = "_labelText_1injp_76";
const styles$g = {
	dialog: dialog,
	modal: modal,
	header: header$2,
	body: body$1,
	bodyContent: bodyContent,
	labelSection: labelSection,
	textarea: textarea$1,
	footer: footer$2,
	labelText: labelText
};

const FeedbackModal = ({
  isOpen,
  title = "Thanks for your feedback!",
  feedbackType,
  position,
  onSubmit,
  onDismiss
}) => {
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRef = useRef(null);
  const textareaRef = useRef(null);
  const handleSubmit = useCallback(() => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    onSubmit(feedbackText);
  }, [feedbackText, onSubmit, isSubmitting]);
  const handleDismiss = useCallback(() => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    onDismiss();
  }, [onDismiss, isSubmitting]);
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );
  const handleDialogClick = useCallback(
    (e) => {
      const dialogDimensions = dialogRef.current?.getBoundingClientRect();
      if (dialogDimensions && (e.clientX < dialogDimensions.left || e.clientX > dialogDimensions.right || e.clientY < dialogDimensions.top || e.clientY > dialogDimensions.bottom)) {
        handleDismiss();
      }
    },
    [handleDismiss]
  );
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) {
      dialog.showModal();
      if (position) {
        dialog.style.left = `${position.left}px`;
        dialog.style.top = `${position.top}px`;
        dialog.style.transform = "none";
      } else {
        dialog.style.left = "50%";
        dialog.style.top = "50%";
        dialog.style.transform = "translate(-50%, -50%)";
      }
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen, position]);
  useEffect(() => {
    if (!isOpen) {
      setFeedbackText("");
      setIsSubmitting(false);
    }
  }, [isOpen]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (e) => {
      e.preventDefault();
      handleDismiss();
    };
    dialog.addEventListener("cancel", handleCancel);
    return () => {
      dialog.removeEventListener("cancel", handleCancel);
    };
  }, [handleDismiss]);
  return /* @__PURE__ */ jsx(
    "dialog",
    {
      ref: dialogRef,
      className: styles$g.dialog,
      onClick: handleDialogClick,
      children: /* @__PURE__ */ jsxs("div", { className: styles$g.modal, onClick: (e) => e.stopPropagation(), children: [
        /* @__PURE__ */ jsxs("div", { className: styles$g.header, children: [
          /* @__PURE__ */ jsx(Text, { type: "ParagraphHeader3", children: title }),
          /* @__PURE__ */ jsx(
            IconButton,
            {
              name: "formX",
              variant: "ghost",
              onPress: handleDismiss,
              "aria-label": "Close modal"
            }
          )
        ] }),
        /* @__PURE__ */ jsx("div", { className: styles$g.body, children: /* @__PURE__ */ jsxs("div", { className: styles$g.bodyContent, children: [
          feedbackType && /* @__PURE__ */ jsxs(Text, { type: "Body1Multiline", children: [
            /* @__PURE__ */ jsx(Text, { type: "Body3Multiline", children: feedbackType === FeedbackType.THUMBS_UP ? `${FeedbackValue.GOOD} response ` : `${FeedbackValue.BAD} response ` }),
            "has been recorded."
          ] }),
          /* @__PURE__ */ jsx("div", { className: styles$g.labelSection, children: /* @__PURE__ */ jsx(Text, { type: "SmallPrint3", className: styles$g.labelText, children: "Add a comment to help us improve" }) }),
          /* @__PURE__ */ jsx(
            "textarea",
            {
              ref: textareaRef,
              className: styles$g.textarea,
              value: feedbackText,
              onChange: (e) => setFeedbackText(e.target.value),
              onKeyDown: handleKeyDown,
              rows: 4
            }
          )
        ] }) }),
        /* @__PURE__ */ jsxs("div", { className: styles$g.footer, children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "secondary",
              size: "medium",
              onPress: handleDismiss,
              isDisabled: isSubmitting,
              children: "Cancel"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "primary",
              size: "medium",
              onPress: handleSubmit,
              isDisabled: isSubmitting || feedbackText.trim() === "",
              children: "Send comment"
            }
          )
        ] })
      ] })
    }
  );
};

const initialState = {
  isOpen: false,
  title: "",
  feedbackType: null,
  position: void 0,
  feedbackData: void 0,
  callback: null
};
const useFeedbackModal = () => {
  const [state, setState] = useState(initialState);
  const openFeedbackModal = useCallback((params) => {
    setState({
      isOpen: true,
      title: params.title || "Thanks for your feedback!",
      feedbackType: params.feedbackType,
      position: params.position,
      feedbackData: params.feedbackData,
      callback: params.callback
    });
  }, []);
  const closeFeedbackModal = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isOpen: false
    }));
  }, []);
  const handleSubmit = useCallback(
    (comment) => {
      if (state.callback) {
        state.callback(comment);
      }
      closeFeedbackModal();
    },
    [state.callback, closeFeedbackModal]
  );
  const handleDismiss = useCallback(() => {
    if (state.callback) {
      state.callback("");
    }
    closeFeedbackModal();
  }, [state.callback, closeFeedbackModal]);
  return {
    isOpen: state.isOpen,
    title: state.title,
    feedbackType: state.feedbackType,
    position: state.position,
    feedbackData: state.feedbackData,
    openFeedbackModal,
    closeFeedbackModal,
    handleSubmit,
    handleDismiss
  };
};

const submitFeedback = async (params) => {
  try {
    const response = await fetch(params.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        spanId: params.spanId,
        rating: params.rating,
        comment: params.comment,
        conversationId: params.conversationId,
        toolCallId: params.toolCallId,
        userMessageId: params.userMessageId,
        messageId: params.messageId,
        toolName: params.toolName,
        metadata: params.metadata
      })
    });
    if (!response.ok) {
      console.error("Failed to submit feedback:", {
        status: response.status,
        statusText: response.statusText
      });
      return false;
    }
    const result = await response.json();
    console.log("Feedback submitted successfully:", result);
    return true;
  } catch (error) {
    console.error("Error submitting feedback:", error);
    return false;
  }
};
const trackFeedbackInteractionEvent = (params) => {
  const commonFields = getCommonTrackingEventFields({
    conversationId: params.conversationId,
    copilotId: params.copilotId,
    userMessageId: params.userMessageId
    // TODO: Add customUserId and accountId in EAI-1766
  });
  trackCopilotInteractionEvent({
    interaction_name: `${toLower(params.feedbackType === FeedbackType.THUMBS_UP ? FeedbackValue.GOOD : FeedbackValue.BAD)}_response`,
    interaction_goal: `submit_${params.toolName === "assistant_message" ? "message" : "tool"}_feedback`,
    interaction_raw_text: `${params.feedbackType === FeedbackType.THUMBS_UP ? FeedbackValue.GOOD : FeedbackValue.BAD} response`,
    event_action: "submit",
    subject: "feedback",
    ...commonFields
  });
};
const createFeedbackCallback = (params) => {
  return async (comment) => {
    const success = await submitFeedback({
      ...params,
      rating: params.feedbackType === FeedbackType.THUMBS_UP ? FEEDBACK_RATINGS.EXPLICIT_GOOD : FEEDBACK_RATINGS.EXPLICIT_BAD,
      comment
    });
    if (success) {
      params.onSuccess?.();
    } else {
      params.onError?.("Failed to submit feedback");
    }
    trackFeedbackInteractionEvent({
      copilotId: params.copilotId,
      conversationId: params.conversationId,
      feedbackType: params.feedbackType,
      toolName: params.toolName,
      userMessageId: params.userMessageId
    });
  };
};

const copilotTextPart = "_copilotTextPart_1u8sf_1";
const partWrapper = "_partWrapper_1u8sf_5";
const userWrapper = "_userWrapper_1u8sf_11";
const userPart = "_userPart_1u8sf_15";
const assistantWrapper = "_assistantWrapper_1u8sf_26";
const assistantPart = "_assistantPart_1u8sf_26";
const markdown = "_markdown_1u8sf_26";
const toolInvocationDebug = "_toolInvocationDebug_1u8sf_87";
const styles$f = {
	copilotTextPart: copilotTextPart,
	partWrapper: partWrapper,
	userWrapper: userWrapper,
	userPart: userPart,
	assistantWrapper: assistantWrapper,
	assistantPart: assistantPart,
	markdown: markdown,
	toolInvocationDebug: toolInvocationDebug
};

const CopilotAssistantMessagePartWrapper = ({ children, className, style }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: clsx(styles$f.partWrapper, styles$f.assistantWrapper, className),
    style,
    children
  }
);
const CopilotUserMessagePartWrapper = ({ children, className, style }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: clsx(styles$f.partWrapper, styles$f.userWrapper, className),
    style,
    children
  }
);
const CopilotMessagePartWrapper = ({
  role,
  ...restProps
}) => role === "user" ? /* @__PURE__ */ jsx(CopilotUserMessagePartWrapper, { ...restProps }) : /* @__PURE__ */ jsx(CopilotAssistantMessagePartWrapper, { ...restProps });
CopilotMessagePartWrapper.displayName = "CopilotMessagePartWrapper";

const CopilotFilePart = ({ part }) => {
  const fileName = part.filename || part.name || "Uploaded file";
  const mimeType = part.mediaType || part.mimeType || part.type || "application/octet-stream";
  const url = part.url || part.data;
  const size = part.size || "large";
  return /* @__PURE__ */ jsx(
    FileAttachment,
    {
      fileName,
      fileType: "",
      mimeType,
      data: url,
      showCloseButton: false,
      size
    }
  );
};

const CopilotMarkdown = ({ children }) => /* @__PURE__ */ jsx(
  Markdown,
  {
    className: styles$f.markdown,
    options: {
      overrides: {
        a: (props) => {
          return /* @__PURE__ */ jsx(Link, { color: "primary", target: "_blank", ...props });
        }
      }
    },
    children
  }
);
const CopilotTextPart = ({
  className,
  isErrored = false,
  message,
  part,
  style
}) => {
  const isUser = message.role === "user";
  const contentClass = isUser ? styles$f.userPart : styles$f.assistantPart;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: clsx(styles$f.copilotTextPart, contentClass, className),
      style,
      "data-is-errored": isErrored ? true : void 0,
      children: isUser ? part.text : /* @__PURE__ */ jsx(CopilotMarkdown, { children: part.text })
    }
  );
};

const truncatedText = "_truncatedText_ruqca_1";
const focusableTrigger = "_focusableTrigger_ruqca_7";
const styles$e = {
	truncatedText: truncatedText,
	focusableTrigger: focusableTrigger
};

const TruncatedText = ({
  children,
  className = ""
}) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [children]);
  if (isTruncated) {
    return /* @__PURE__ */ jsx(Tooltip, { allowMultiline: true, content: isTruncated ? children : "", children: /* @__PURE__ */ jsx(Tooltip.FocusableTrigger, { className: styles$e.focusableTrigger, children: /* @__PURE__ */ jsx("div", { ref: textRef, className: clsx(styles$e.truncatedText, className), children }) }) });
  }
  return /* @__PURE__ */ jsx("div", { ref: textRef, className: clsx(styles$e.truncatedText, className), children });
};

const toolCall = "_toolCall_1xw5l_1";
const statusIconContainer = "_statusIconContainer_1xw5l_13";
const actions$1 = "_actions_1xw5l_16";
const toolCallText = "_toolCallText_1xw5l_36";
const action$1 = "_action_1xw5l_16";
const spinnerWrapper = "_spinnerWrapper_1xw5l_55";
const iconWrapper = "_iconWrapper_1xw5l_60";
const styles$d = {
	toolCall: toolCall,
	statusIconContainer: statusIconContainer,
	actions: actions$1,
	toolCallText: toolCallText,
	action: action$1,
	spinnerWrapper: spinnerWrapper,
	iconWrapper: iconWrapper
};

const ToolCall = ({
  className,
  feedback,
  iconColor = "UiPrimary",
  iconName = "actionBuild",
  isDebug = false,
  onDebugPress,
  onFeedbackPress,
  onTestConfirmPress,
  onTestSkipPress,
  status,
  style,
  text
}) => {
  const StatusIcon = () => {
    switch (status) {
      case "loading":
        return /* @__PURE__ */ jsx("div", { className: styles$d.spinnerWrapper, children: /* @__PURE__ */ jsx(Spinner, { size: "small" }) });
      case "stopped":
        return /* @__PURE__ */ jsx(Icon, { name: "statusStopFill", color: "TextWeakest", size: 20 });
      case "error":
        return /* @__PURE__ */ jsx(Icon, { name: "formXCircleOutlined", color: "StatusError", size: 20 });
      case "test-paused":
        return /* @__PURE__ */ jsx(Icon, { name: "statusPaused", color: "TextWeakest", size: 20 });
      case "test-skipped":
        return /* @__PURE__ */ jsx(Tag, { variant: "simple", children: "Skipped" });
      default:
        return /* @__PURE__ */ jsx(Icon, { name: "formCheck", color: "StatusSuccess", size: 20 });
    }
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: clsx(styles$d.toolCall, className),
      "data-loading": status === "loading" ? true : void 0,
      "data-debug-enabled": isDebug ? true : void 0,
      style,
      children: [
        /* @__PURE__ */ jsx("div", { className: styles$d.iconWrapper, children: /* @__PURE__ */ jsx(Icon, { name: iconName, color: iconColor, size: 20 }) }),
        /* @__PURE__ */ jsx(TruncatedText, { className: styles$d.toolCallText, children: text }),
        status === "test-confirm" ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Button, { onPress: onTestSkipPress, variant: "secondary", size: "compact", children: "Skip" }),
          /* @__PURE__ */ jsx(Button, { onPress: onTestConfirmPress, variant: "primary", size: "compact", children: "Test" })
        ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("div", { className: styles$d.statusIconContainer, children: /* @__PURE__ */ jsx(StatusIcon, {}) }),
          /* @__PURE__ */ jsxs("div", { className: styles$d.actions, children: [
            isDebug && /* @__PURE__ */ jsx(Tooltip, { content: `Toggle debug details`, children: /* @__PURE__ */ jsx(
              IconButton,
              {
                "aria-label": `Toggle debug details`,
                name: "actionBug",
                onPress: onDebugPress,
                size: "small",
                variant: "ghost"
              }
            ) }),
            status !== "loading" && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(
                Tooltip,
                {
                  content: `Good response${feedback === FeedbackSentiment.POSITIVE ? " sent" : ""}`,
                  children: /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      "aria-label": feedback === FeedbackSentiment.POSITIVE ? "Positive feedback sent" : "Give response positive feedback",
                      className: styles$d.action,
                      isSelected: feedback === FeedbackSentiment.POSITIVE ? true : void 0,
                      name: feedback === FeedbackSentiment.POSITIVE ? "ratingThumbsUpFill" : "ratingThumbsUp",
                      size: "small",
                      variant: "ghost",
                      onPress: () => onFeedbackPress && onFeedbackPress(FeedbackSentiment.POSITIVE)
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx(
                Tooltip,
                {
                  content: `Bad response${feedback === FeedbackSentiment.NEGATIVE ? " sent" : ""}`,
                  children: /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      "aria-label": feedback === FeedbackSentiment.NEGATIVE ? "Negative feedback sent" : "Give response negative feedback",
                      className: styles$d.action,
                      isSelected: feedback === FeedbackSentiment.NEGATIVE ? true : void 0,
                      name: feedback === FeedbackSentiment.NEGATIVE ? "ratingThumbsDownFill" : "ratingThumbsDown",
                      size: "small",
                      variant: "ghost",
                      onPress: () => onFeedbackPress && onFeedbackPress(FeedbackSentiment.NEGATIVE)
                    }
                  )
                }
              )
            ] })
          ] })
        ] })
      ]
    }
  );
};

const camelSnakeToTitle = (str) => {
  if (!str) return "";
  let result = str.replace(/_/g, " ");
  result = result.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  result = result.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  return result.split(" ").filter((word) => word.length > 0).map((word) => {
    if (word.length === 1) {
      return word.toUpperCase();
    }
    if (word === word.toUpperCase() && word.length > 1) {
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(" ");
};
const getToolCallStatus = (toolInvocation, displayOverride) => {
  if (toolInvocation.state !== "result") {
    return "loading";
  }
  if (displayOverride.isError) {
    return "error";
  }
  if ("result" in toolInvocation && toolInvocation.result?.content?.[0]?.text === "Tool cancelled by user") {
    return "stopped";
  }
  return "success";
};
const getToolCallIconName = (toolName) => {
  if (/custom[_-]?action/i.test(toolName)) {
    return "navCustomAction";
  }
  if (/fill[_-]?out/i.test(toolName)) {
    return "formTextAlignLeft";
  }
  if (/validate/i.test(toolName)) {
    return "actionCompare";
  }
  if (/roll[_-]?back|checkpoint/i.test(toolName)) {
    return "miscTimelapse";
  }
  if (/delete|remove/i.test(toolName)) {
    return "actionTrash";
  }
  if (/auth/i.test(toolName)) {
    return "personSettings";
  }
  if (/mapping/i.test(toolName)) {
    return "formTextAlignLeft";
  }
  if (/test/i.test(toolName)) {
    return "actionBug";
  }
  if (/search|docs/i.test(toolName)) {
    return "navSearch";
  }
  if (/add|update|edit/i.test(toolName)) {
    return "actionEdit";
  }
  if (/rename/i.test(toolName)) {
    return "actionRename";
  }
  if (/suggest/i.test(toolName)) {
    return "miscAI";
  }
  return "actionBuild";
};
const getToolCallIconColor = (toolName) => {
  if (/delete|remove/i.test(toolName)) {
    return "StatusError";
  }
  return "UiPrimary";
};

const DebugPanel = ({ part }) => {
  const { toolInvocation } = part;
  return /* @__PURE__ */ jsxs("div", { className: styles$f.toolInvocationDebug, children: [
    /* @__PURE__ */ jsx("h3", { children: /* @__PURE__ */ jsx("strong", { children: "Tool Name" }) }),
    /* @__PURE__ */ jsx("pre", { children: toolInvocation.toolName }),
    /* @__PURE__ */ jsx("h3", { children: /* @__PURE__ */ jsx("strong", { children: "Args" }) }),
    /* @__PURE__ */ jsx("pre", { children: JSON.stringify(toolInvocation.args, null, 2) }),
    /* @__PURE__ */ jsx("h3", { children: /* @__PURE__ */ jsx("strong", { children: "Result" }) }),
    /* @__PURE__ */ jsx("pre", { children: JSON.stringify(
      toolInvocation.state === "result" ? toolInvocation.result : void 0,
      null,
      2
    ) })
  ] });
};
const defaultGetToolCallDisplay = (_message, part) => {
  let isError = false;
  let message = void 0;
  if (part.toolInvocation.args?.displayMessage) {
    message = part.toolInvocation.args.displayMessage;
  } else if (part.toolInvocation.state === "partial-call" && Object.keys(part.toolInvocation.args || {}).length === 0) {
    message = "...";
  }
  if (part.toolInvocation.state === "result" && part.toolInvocation.result?.$error?.message) {
    isError = true;
    message = part.toolInvocation.result.$error.message;
  }
  return { message, isError };
};
const CopilotToolPart = ({
  className,
  getToolCallDisplay = defaultGetToolCallDisplay,
  isDebug = false,
  message,
  part,
  style
}) => {
  const feedbackConfig = useFeedbackConfig();
  const context = useContext(CopilotContext);
  const messageSpanIds = context?.messageSpanIds || /* @__PURE__ */ new Map();
  const [feedback, setFeedback] = useState(null);
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const toolCallRef = useRef(null);
  const {
    isOpen,
    title,
    feedbackType,
    position,
    openFeedbackModal,
    handleSubmit,
    handleDismiss
  } = useFeedbackModal();
  const { toolInvocation } = part;
  const handleFeedbackActionClick = async (feedback2) => {
    setFeedback(feedback2);
    const messageSpanId = messageSpanIds.get(message.id);
    const spanIdToUse = messageSpanId || context?.spanId;
    if (feedbackConfig?.url && spanIdToUse) {
      const toolCallRect = toolCallRef.current?.getBoundingClientRect();
      const modalPosition = toolCallRect ? {
        top: toolCallRect.bottom + 10,
        left: toolCallRect.left
      } : void 0;
      const feedbackCallback = createFeedbackCallback({
        url: feedbackConfig.url,
        spanId: spanIdToUse,
        copilotId: context?.copilotId,
        conversationId: context?.conversationId,
        feedbackType: feedback2 === FeedbackSentiment.POSITIVE ? FeedbackType.THUMBS_UP : FeedbackType.THUMBS_DOWN,
        toolCallId: toolInvocation.toolCallId,
        toolName: toolInvocation.toolName || "unknown_tool",
        onSuccess: feedbackConfig.onSuccess || (() => {
        }),
        onError: feedbackConfig.onError || (() => {
        })
      });
      openFeedbackModal({
        feedbackType: feedback2 === FeedbackSentiment.POSITIVE ? FeedbackType.THUMBS_UP : FeedbackType.THUMBS_DOWN,
        position: modalPosition,
        callback: feedbackCallback,
        feedbackData: {
          toolCallId: toolInvocation.toolCallId,
          toolName: toolInvocation.toolName,
          spanId: spanIdToUse
        }
      });
    }
  };
  const displayOverride = getToolCallDisplay?.(message, part) ?? {};
  const status = getToolCallStatus(toolInvocation, displayOverride);
  const getText = () => {
    if (displayOverride.message) {
      return displayOverride.message;
    }
    const formattedToolName = camelSnakeToTitle(toolInvocation.toolName);
    switch (status) {
      case "error":
        return `${formattedToolName} errored`;
      case "loading":
        return `${formattedToolName} being called...`;
      case "stopped":
        return `${formattedToolName} stopped`;
      default:
        return `${formattedToolName} called`;
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { ref: toolCallRef, children: /* @__PURE__ */ jsx(
      ToolCall,
      {
        className: clsx(styles$f.toolCall, className),
        feedback,
        onDebugPress: () => setIsDebugPanelOpen(!isDebugPanelOpen),
        onFeedbackPress: handleFeedbackActionClick,
        iconColor: displayOverride.iconColor ?? getToolCallIconColor(toolInvocation.toolName),
        iconName: displayOverride.icon ?? getToolCallIconName(toolInvocation.toolName),
        isDebug,
        text: getText(),
        status: getToolCallStatus(toolInvocation, displayOverride),
        style
      }
    ) }),
    isDebugPanelOpen && /* @__PURE__ */ jsx(DebugPanel, { part }),
    /* @__PURE__ */ jsx(
      FeedbackModal,
      {
        isOpen,
        title,
        feedbackType,
        position,
        onSubmit: handleSubmit,
        onDismiss: handleDismiss
      }
    )
  ] });
};

const CopilotMessagePart = ({
  message,
  part,
  className,
  style,
  renderPartContent,
  isDebug,
  isErrored = false,
  getToolCallDisplay
}) => {
  const override = renderPartContent?.(message, part);
  if (part.type === "step-start" && !override) {
    return void 0;
  }
  const renderDefault = () => {
    switch (part.type) {
      case "text":
        return /* @__PURE__ */ jsx(
          CopilotTextPart,
          {
            isErrored,
            part,
            message
          }
        );
      case "tool-invocation":
        return /* @__PURE__ */ jsx(
          CopilotToolPart,
          {
            part,
            message,
            isDebug,
            getToolCallDisplay
          }
        );
      case "file":
        return /* @__PURE__ */ jsx(CopilotFilePart, { part, message });
      // In the future we'll add other part types: reasoning, etc.
      default:
        return null;
    }
  };
  return /* @__PURE__ */ jsx(
    CopilotMessagePartWrapper,
    {
      role: message.role,
      className,
      style,
      children: override !== void 0 ? override : renderDefault()
    }
  );
};

const message = "_message_lqslj_1";
const actions = "_actions_lqslj_11";
const action = "_action_lqslj_11";
const userActions = "_userActions_lqslj_20";
const messageToolbar = "_message-toolbar_lqslj_23";
const errorWrapper = "_error-wrapper_lqslj_27";
const errorMessage = "_error-message_lqslj_34";
const retryButton = "_retry-button_lqslj_40";
const timestamp = "_timestamp_lqslj_52";
const edit = "_edit_lqslj_57";
const editTextarea = "_edit-textarea_lqslj_64";
const editActions = "_edit-actions_lqslj_80";
const attachments = "_attachments_lqslj_85";
const styles$c = {
	message: message,
	actions: actions,
	action: action,
	userActions: userActions,
	messageToolbar: messageToolbar,
	errorWrapper: errorWrapper,
	errorMessage: errorMessage,
	retryButton: retryButton,
	timestamp: timestamp,
	edit: edit,
	editTextarea: editTextarea,
	editActions: editActions,
	attachments: attachments
};

const hasExperimentalAttachments = (message) => {
  return "experimental_attachments" in message && Array.isArray(message.experimental_attachments);
};
const getExperimentalAttachments = (message) => {
  return hasExperimentalAttachments(message) ? message.experimental_attachments : [];
};
const createDataUrl = (attachment, index) => {
  const fileName = attachment.name || `file-${index}`;
  const contentType = attachment.contentType || "application/octet-stream";
  const rawData = attachment.data || attachment.content || attachment.base64 || attachment.url;
  let dataUrl = "";
  if (rawData) {
    if (rawData.startsWith("data:")) {
      dataUrl = rawData;
    } else {
      dataUrl = `data:${contentType};base64,${rawData}`;
    }
  }
  return { dataUrl, fileName, contentType };
};
const FileAttachmentList = ({
  message
}) => {
  const experimental_attachments = getExperimentalAttachments(message);
  if (experimental_attachments.length === 0) {
    return null;
  }
  try {
    return /* @__PURE__ */ jsx("div", { className: styles$c.attachments, children: experimental_attachments.map((attachment, index) => {
      try {
        if (!attachment || typeof attachment !== "object") {
          console.warn(
            "Invalid attachment object at index",
            index,
            attachment
          );
          return null;
        }
        const { dataUrl, fileName, contentType } = createDataUrl(
          attachment,
          index
        );
        const attachmentSize = experimental_attachments.length === 1 ? "large" : "medium";
        return /* @__PURE__ */ jsx(
          CopilotFilePart,
          {
            message,
            part: {
              type: "file",
              filename: fileName,
              mediaType: contentType,
              url: dataUrl,
              size: attachmentSize
              // Pass size to the part
            }
          },
          `${message.id}-attachment-${index}`
        );
      } catch (error) {
        console.error(
          "Error creating file attachment:",
          error,
          attachment
        );
        return null;
      }
    }).filter(Boolean) });
  } catch (error) {
    console.error("Error processing experimental_attachments:", error);
    return null;
  }
};

function useEditMessage(message) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const context = useContext(CopilotContext);
  const startEditing = () => {
    setIsEditing(true);
    setEditedContent(message.content);
  };
  const cancelEditing = () => {
    setIsEditing(false);
    setEditedContent("");
  };
  const submitEdit = () => {
    if (!context) return;
    const messageIndex = context.messages.findIndex(
      (msg) => msg.id === message.id
    );
    if (messageIndex === -1) return;
    const updatedMessages = context.messages.map((msg) => {
      if (msg.id === message.id) {
        return {
          ...msg,
          content: editedContent,
          parts: [
            {
              type: "text",
              text: editedContent
            }
          ]
        };
      }
      return msg;
    });
    const messagesToKeep = updatedMessages.slice(0, messageIndex + 1);
    context.setMessages(messagesToKeep);
    context.reload();
    setIsEditing(false);
  };
  return {
    cancelEditing,
    editedContent,
    isEditing,
    setEditedContent,
    startEditing,
    submitEdit
  };
}

const getFormattedTimestamp = (date) => {
  let dateObj;
  if (typeof date === "string") {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }
  const now = /* @__PURE__ */ new Date();
  const diffInMs = now.getTime() - dateObj.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1e3 * 60));
  const diffInHours = Math.floor(diffInMs / (1e3 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1e3 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInYears = Math.floor(diffInDays / 365);
  if (diffInMinutes < 1) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} min ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hr${diffInHours === 1 ? "" : "s"} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} d ago`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks} wk ago`;
  } else if (diffInDays >= 28 && diffInDays < 365) {
    const months = Math.max(1, Math.floor(diffInDays / 30));
    return `${months} mo ago`;
  } else {
    return `${diffInYears} yr ago`;
  }
};

const getPartKey = (message, part, index) => {
  if (part.type === "text" && part.text) {
    return `${message.id}-${part.type}-${index}-${part.text.substring(0, 10)}`;
  }
  return `${message.id}-${part.type}-${index}`;
};
const CopilotMessage = ({
  className,
  getToolCallDisplay,
  isDebug,
  // TODO: Figure out how to place the message into this error state
  isErrored = false,
  message,
  renderPart,
  renderPartContent,
  style,
  userMessageId,
  ...props
}) => {
  const [isCopying, setIsCopying] = useState(false);
  const {
    cancelEditing,
    editedContent,
    isEditing,
    setEditedContent,
    startEditing,
    submitEdit
  } = useEditMessage(message);
  const feedbackConfig = useFeedbackConfig();
  const context = useContext(CopilotContext);
  const messageSpanIds = context?.messageSpanIds || /* @__PURE__ */ new Map();
  const [feedback, setFeedback] = useState(null);
  const feedbackButtonRef = useRef(null);
  const {
    isOpen,
    title,
    feedbackType,
    position,
    openFeedbackModal,
    handleSubmit,
    handleDismiss
  } = useFeedbackModal();
  const { content, parts, role } = message;
  const isUser = role === "user";
  const isAssistant = role === "assistant";
  const createdAt = message.createdAt;
  const handleFeedbackActionClick = async (feedback2) => {
    setFeedback(feedback2);
    const messageSpanId = messageSpanIds.get(message.id);
    const spanIdToUse = messageSpanId || context?.spanId;
    if (feedbackConfig?.url && spanIdToUse) {
      const buttonRect = feedbackButtonRef.current?.getBoundingClientRect();
      const modalPosition = buttonRect ? (() => {
        const modalHeight = 250;
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        if (spaceBelow < modalHeight + 20 && spaceAbove >= modalHeight + 20) {
          return {
            top: buttonRect.top - modalHeight - 10,
            left: buttonRect.left - 200
            // Offset to center modal near button
          };
        }
        return {
          top: buttonRect.bottom + 10,
          left: buttonRect.left - 200
          // Offset to center modal near button
        };
      })() : void 0;
      const feedbackCallback = createFeedbackCallback({
        url: feedbackConfig.url,
        spanId: spanIdToUse,
        copilotId: context?.copilotId,
        conversationId: context?.conversationId,
        feedbackType: feedback2 === FeedbackSentiment.POSITIVE ? FeedbackType.THUMBS_UP : FeedbackType.THUMBS_DOWN,
        userMessageId,
        messageId: message.id,
        toolName: "assistant_message",
        onSuccess: feedbackConfig.onSuccess || (() => {
        }),
        onError: feedbackConfig.onError || (() => {
        })
      });
      openFeedbackModal({
        feedbackType: feedback2 === FeedbackSentiment.POSITIVE ? FeedbackType.THUMBS_UP : FeedbackType.THUMBS_DOWN,
        position: modalPosition,
        callback: feedbackCallback,
        feedbackData: {
          messageId: message.id,
          spanId: spanIdToUse
        }
      });
    }
  };
  const trackCopyInteractionEvent = useCallback(() => {
    const commonFields = getCommonTrackingEventFields({
      conversationId: context?.conversationId,
      copilotId: context?.copilotId,
      userMessageId
      // TODO: Add customUserId and accountId in EAI-1766
    });
    trackCopilotInteractionEvent({
      interaction_name: "copy_message",
      interaction_goal: `copy_${role}_message`,
      interaction_raw_text: "Copy",
      event_action: "click",
      subject: "message",
      ...commonFields
    });
  }, [context?.copilotId, context?.conversationId]);
  const handleCopyClick = async () => {
    if (isCopying) {
      return;
    }
    await navigator.clipboard.writeText(content);
    setIsCopying(true);
    trackCopyInteractionEvent();
    setTimeout(() => {
      setIsCopying(false);
    }, 4e3);
  };
  const cleanedParts = parts.length > 0 ? parts : [
    {
      type: "text",
      text: content
    }
  ];
  if (isEditing) {
    return /* @__PURE__ */ jsxs("div", { className: styles$c.edit, children: [
      /* @__PURE__ */ jsx(
        "textarea",
        {
          className: styles$c.editTextarea,
          value: editedContent,
          onChange: (e) => setEditedContent(e.target.value)
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: styles$c.editActions, children: [
        /* @__PURE__ */ jsx(Button, { onPress: cancelEditing, size: "small", variant: "secondary", children: "Cancel" }),
        /* @__PURE__ */ jsx(Button, { onPress: submitEdit, size: "small", variant: "primary", children: "Send" })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: clsx([styles$c.message, className]),
      "data-role": message.role,
      style,
      ...props,
      children: [
        /* @__PURE__ */ jsx(FileAttachmentList, { message }),
        cleanedParts.map((part, index) => {
          const key = getPartKey(message, part, index);
          const rendered = renderPart ? renderPart(message, part, index) : void 0;
          return /* @__PURE__ */ jsx(React.Fragment, { children: rendered ?? /* @__PURE__ */ jsx(
            CopilotMessagePart,
            {
              isErrored,
              message,
              part,
              isDebug,
              renderPartContent,
              getToolCallDisplay
            }
          ) }, key);
        }),
        isErrored && isUser ? /* @__PURE__ */ jsxs("div", { className: styles$c.errorWrapper, children: [
          /* @__PURE__ */ jsxs("span", { className: styles$c.errorMessage, children: [
            /* @__PURE__ */ jsx(Icon, { color: "StatusError", name: "formXCircle", size: "14" }),
            "Could not send message"
          ] }),
          /* @__PURE__ */ jsxs(
            Button,
            {
              onPress: () => context?.reload(),
              size: "small",
              variant: "tertiary",
              children: [
                /* @__PURE__ */ jsx(Icon, { name: "arrowRefresh", size: "14" }),
                "Retry"
              ]
            }
          )
        ] }) : /* @__PURE__ */ jsxs("div", { className: styles$c.messageToolbar, children: [
          /* @__PURE__ */ jsx("span", { className: styles$c.timestamp, children: createdAt ? getFormattedTimestamp(createdAt) : "" }),
          /* @__PURE__ */ jsxs("div", { className: clsx(styles$c.actions, isUser && styles$c.userActions), children: [
            /* @__PURE__ */ jsx(Tooltip, { content: isCopying ? "Copied to clipboard" : "Copy", children: /* @__PURE__ */ jsx(
              IconButton,
              {
                "aria-label": isCopying ? "Copied message to clipboard" : "Copy",
                className: styles$c.action,
                name: isCopying ? "formCheck" : "actionCopy",
                onPress: handleCopyClick,
                size: "small",
                variant: "ghost"
              }
            ) }),
            isUser && /* @__PURE__ */ jsx(Tooltip, { content: "Edit message", children: /* @__PURE__ */ jsx(
              IconButton,
              {
                "aria-label": "Edit message",
                className: styles$c.action,
                name: "actionEdit",
                onPress: startEditing,
                size: "small",
                variant: "ghost"
              }
            ) }),
            isAssistant && feedbackConfig && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(
                Tooltip,
                {
                  content: `Good response${feedback === FeedbackSentiment.POSITIVE ? " sent" : ""}`,
                  children: /* @__PURE__ */ jsx("div", { ref: feedbackButtonRef, children: /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      "aria-label": feedback === FeedbackSentiment.POSITIVE ? "Positive feedback sent" : "Give response positive feedback",
                      className: styles$c.action,
                      "data-selected": feedback === FeedbackSentiment.POSITIVE ? true : void 0,
                      isSelected: feedback === FeedbackSentiment.POSITIVE ? true : void 0,
                      name: feedback === FeedbackSentiment.POSITIVE ? "ratingThumbsUpFill" : "ratingThumbsUp",
                      onPress: () => handleFeedbackActionClick(FeedbackSentiment.POSITIVE),
                      size: "small",
                      variant: "ghost"
                    }
                  ) })
                }
              ),
              /* @__PURE__ */ jsx(
                Tooltip,
                {
                  content: `Bad response${feedback === FeedbackSentiment.NEGATIVE ? " sent" : ""}`,
                  children: /* @__PURE__ */ jsx(
                    IconButton,
                    {
                      "aria-label": feedback === FeedbackSentiment.NEGATIVE ? "Negative feedback sent" : "Give response negative feedback",
                      className: styles$c.action,
                      isSelected: feedback === FeedbackSentiment.NEGATIVE ? true : void 0,
                      name: feedback === FeedbackSentiment.NEGATIVE ? "ratingThumbsDownFill" : "ratingThumbsDown",
                      onPress: () => handleFeedbackActionClick(FeedbackSentiment.NEGATIVE),
                      size: "small",
                      variant: "ghost"
                    }
                  )
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx(
          FeedbackModal,
          {
            isOpen,
            title,
            feedbackType,
            position,
            onSubmit: handleSubmit,
            onDismiss: handleDismiss
          }
        )
      ]
    }
  );
};

const messageList = "_messageList_1cux6_1";
const hideScrollbar = "_hideScrollbar_1cux6_24";
const thinkingIndicator = "_thinkingIndicator_1cux6_32";
const messageListWrapper = "_messageListWrapper_1cux6_50";
const messageListItem = "_messageListItem_1cux6_56";
const styles$b = {
	messageList: messageList,
	hideScrollbar: hideScrollbar,
	thinkingIndicator: thinkingIndicator,
	messageListWrapper: messageListWrapper,
	messageListItem: messageListItem
};

const text = "_text_1wsxw_1";
const thinking = "_thinking_1wsxw_6";
const styles$a = {
	text: text,
	thinking: thinking};

const Thinking = ({ text = "Thinking..." }) => {
  return /* @__PURE__ */ jsx("div", { className: styles$a.thinking, children: /* @__PURE__ */ jsx(Text, { type: "Body1", className: styles$a.text, children: text }) });
};

function computeUserMessageIds(messages) {
  const result = [];
  let lastUserId = void 0;
  for (const msg of messages) {
    if (msg.role === "user") {
      lastUserId = msg.id;
    }
    result.push(lastUserId);
  }
  return result;
}
const CopilotMessageList = forwardRef((props, ref) => {
  const {
    autoScroll = true,
    className,
    error,
    getToolCallDisplay,
    isDebug,
    messages,
    renderMessage,
    renderPart,
    renderPartContent,
    status,
    style
  } = props;
  const innerRef = useRef(null);
  const userMessageIds = useMemo(
    () => computeUserMessageIds(messages),
    [messages]
  );
  useImperativeHandle(
    ref,
    () => innerRef.current,
    []
  );
  const isUserAtBottomRef = useRef(true);
  useEffect(() => {
    if (!autoScroll) return;
    const container = innerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const threshold = 8;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
      isUserAtBottomRef.current = isAtBottom;
      if (isAtBottom) {
        container.classList.add(styles$b.hideScrollbar);
      } else {
        container.classList.remove(styles$b.hideScrollbar);
      }
    };
    container.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [autoScroll]);
  useEffect(() => {
    if (!autoScroll) return;
    const container = innerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
    isUserAtBottomRef.current = true;
    container.classList.add(styles$b.hideScrollbar);
  }, [autoScroll]);
  useEffect(() => {
    if (!autoScroll) return;
    const container = innerRef.current;
    if (!container) return;
    if (isUserAtBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, autoScroll]);
  const filteredMessages = messages.filter(
    (m) => !m.id.startsWith(HANDOFF_DATA_MESSAGE_ID_PREFIX)
  );
  const renderSingleMessage = useCallback(
    (message, index) => {
      const customRendered = renderMessage ? renderMessage(message, index) : void 0;
      const userMessageId = message.role === "assistant" ? userMessageIds[index] : void 0;
      return /* @__PURE__ */ jsx(React.Fragment, { children: customRendered !== void 0 ? customRendered : /* @__PURE__ */ jsx("li", { className: styles$b.messageListItem, "data-role": message.role, children: /* @__PURE__ */ jsx(
        CopilotMessage,
        {
          message,
          renderPart,
          isDebug,
          renderPartContent,
          getToolCallDisplay,
          userMessageId
        }
      ) }) }, message.id);
    },
    [
      renderMessage,
      renderPart,
      isDebug,
      renderPartContent,
      getToolCallDisplay,
      userMessageIds
    ]
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: innerRef,
      className: clsx(styles$b.messageList, className, {
        [styles$b.hideScrollbar]: isUserAtBottomRef.current
      }),
      style,
      "data-zds": true,
      children: [
        /* @__PURE__ */ jsx("ul", { className: styles$b.messageListWrapper, children: filteredMessages.map(renderSingleMessage) }),
        status === "submitted" && /* @__PURE__ */ jsx("div", { className: styles$b.thinkingIndicator, "data-zds": true, children: /* @__PURE__ */ jsx(Thinking, {}) }),
        error && /* @__PURE__ */ jsx(
          CopilotAlert,
          {
            status: "error",
            title: error.header || "Copilot isn't responding right now",
            message: error.message,
            actions: [
              {
                children: /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx(Icon, { name: "arrowRefresh" }),
                  " Refresh"
                ] }),
                variant: "primary",
                iconPosition: "start",
                onPress: error.onRetry
              }
            ]
          }
        )
      ]
    }
  );
});

const thought = "_thought_11mb7_6";
const summary = "_summary_11mb7_12";
const summaryButton = "_summaryButton_11mb7_12";
const reasoning = "_reasoning_11mb7_29";
const styles$9 = {
	thought: thought,
	summary: summary,
	summaryButton: summaryButton,
	reasoning: reasoning
};

function msToHumanIntl(ms, locale = "en") {
  const absMs = Math.abs(ms);
  let value;
  let unit;
  if (absMs < 6e4) {
    value = absMs / 1e3;
    unit = "second";
  } else if (absMs < 36e5) {
    value = absMs / 6e4;
    unit = "minute";
  } else {
    value = absMs / 36e5;
    unit = "hour";
  }
  const nf = new Intl.NumberFormat(locale, {
    style: "unit",
    unit,
    unitDisplay: "long",
    // "seconds" instead of "s"
    maximumFractionDigits: 0
  });
  return nf.format(Math.floor(value));
}
const Thought = ({ durationMs, reasoning }) => {
  const [isOpen, setIsOpen] = useState(false);
  const id = useId();
  const elementId = `reasoning-${id}`;
  const hasReasoning = !!reasoning && reasoning.trim().length > 0;
  const handleClick = () => {
    if (hasReasoning) {
      setIsOpen(!isOpen);
    }
  };
  const TextContent = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Text, { type: "Body1", color: "TextWeaker", children: "Thought for" }),
    /* @__PURE__ */ jsx(Text, { type: "Body3", color: "TextWeaker", children: msToHumanIntl(durationMs) })
  ] });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: styles$9.thought,
      "data-has-reasoning": hasReasoning ? "true" : "false",
      children: [
        hasReasoning ? /* @__PURE__ */ jsxs(
          "button",
          {
            className: styles$9.summaryButton,
            "aria-label": "Expand thinking summary",
            "aria-controls": elementId,
            "aria-expanded": isOpen,
            onClick: handleClick,
            children: [
              TextContent,
              /* @__PURE__ */ jsx(
                Icon,
                {
                  color: "TextWeaker",
                  size: 16,
                  "aria-hidden": true,
                  name: isOpen ? "arrowSmallDown" : "arrowSmallRight"
                }
              )
            ]
          }
        ) : /* @__PURE__ */ jsx("div", { className: styles$9.summary, children: TextContent }),
        hasReasoning && isOpen && /* @__PURE__ */ jsx(Markdown, { id: elementId, className: styles$9.reasoning, children: reasoning })
      ]
    }
  );
};

const wrapper = "_wrapper_xvlc6_1";
const header$1 = "_header_xvlc6_8";
const body = "_body_xvlc6_16";
const assetPreview = "_asset-preview_xvlc6_20";
const assetPreviewImage = "_asset-preview-image_xvlc6_28";
const externalIcon = "_external-icon_xvlc6_33";
const metadata = "_metadata_xvlc6_43";
const footer$1 = "_footer_xvlc6_50";
const styles$8 = {
	wrapper: wrapper,
	header: header$1,
	body: body,
	assetPreview: assetPreview,
	assetPreviewImage: assetPreviewImage,
	externalIcon: externalIcon,
	metadata: metadata,
	footer: footer$1
};

const HandoffReceived = ({
  title,
  messageCount,
  body,
  buttonText,
  assetPreviewUrl
}) => {
  return /* @__PURE__ */ jsxs("div", { className: styles$8.wrapper, children: [
    /* @__PURE__ */ jsxs("div", { className: styles$8.header, children: [
      /* @__PURE__ */ jsx(Icon$1, { size: 16, color: "BrandOrange", name: "miscBoltAltFill" }),
      /* @__PURE__ */ jsx(Text, { type: "ParagraphHeader2", children: title })
    ] }),
    body && /* @__PURE__ */ jsx("div", { className: styles$8.body, children: /* @__PURE__ */ jsx(Text, { type: "Body1Multiline", children: body }) }),
    assetPreviewUrl && /* @__PURE__ */ jsxs("div", { className: styles$8.assetPreview, children: [
      /* @__PURE__ */ jsx("img", { src: assetPreviewUrl, className: styles$8.assetPreviewImage }),
      /* @__PURE__ */ jsx(Icon$1, { className: styles$8.externalIcon, name: "arrowOffsite", size: 20 })
    ] }),
    /* @__PURE__ */ jsx("div", { className: styles$8.metadata, children: /* @__PURE__ */ jsxs(Text, { type: "SmallPrint1Multiline", color: "TextWeakest", children: [
      messageCount,
      " ",
      messageCount === 1 ? "message" : "messages",
      " were carried over"
    ] }) }),
    /* @__PURE__ */ jsx("div", { className: styles$8.footer, children: /* @__PURE__ */ jsxs(Button, { variant: "primary", size: "compact", iconPosition: "start", children: [
      /* @__PURE__ */ jsx(Icon$1, { name: "formCheck" }),
      buttonText
    ] }) })
  ] });
};

const container$2 = "_container_yt5l2_1";
const icon = "_icon_yt5l2_7";
const styles$7 = {
	container: container$2,
	icon: icon
};

const CopilotIcon = ({ size = 20 }) => /* @__PURE__ */ jsx(
  "div",
  {
    className: styles$7.container,
    style: {
      width: `${size}px`,
      height: `${size}px`
    },
    children: /* @__PURE__ */ jsx("div", { className: styles$7.icon, children: /* @__PURE__ */ jsx(Icon, { name: "miscCopilot", color: "GrayWarm10", size }) })
  }
);

const container$1 = "_container_ou81v_1";
const group = "_group_ou81v_11";
const iconTextWrapper = "_iconTextWrapper_ou81v_17";
const children = "_children_ou81v_23";
const styles$6 = {
	container: container$1,
	group: group,
	iconTextWrapper: iconTextWrapper,
	children: children
};

const Header = ({ children, className, style }) => /* @__PURE__ */ jsxs("div", { className: clsx$1(styles$6.container, className), style, children: [
  /* @__PURE__ */ jsxs("div", { className: styles$6.group, children: [
    /* @__PURE__ */ jsxs("div", { className: styles$6.iconTextWrapper, children: [
      /* @__PURE__ */ jsx(CopilotIcon, {}),
      /* @__PURE__ */ jsx(Text, { type: "Body4", fontWeight: 600, color: "TextStronger", children: "Copilot" })
    ] }),
    /* @__PURE__ */ jsx(Tag, { variant: "simple", size: "x-small", children: "AI beta" })
  ] }),
  /* @__PURE__ */ jsx("div", { className: styles$6.children, children })
] });

const container = "_container_1bnlq_1";
const actionsContainer = "_actionsContainer_1bnlq_9";
const textarea = "_textarea_1bnlq_20";
const footer = "_footer_1bnlq_41";
const footerLabel = "_footerLabel_1bnlq_50";
const footerSection = "_footerSection_1bnlq_76";
const braintrustLink = "_braintrustLink_1bnlq_81";
const styles$5 = {
	container: container,
	actionsContainer: actionsContainer,
	textarea: textarea,
	footer: footer,
	footerLabel: footerLabel,
	footerSection: footerSection,
	braintrustLink: braintrustLink
};

function ViewMessagesJSON() {
  const {
    messages,
    setMessages,
    conversationId,
    spanId,
    braintrustProjectName
  } = useCopilotChat();
  const [jsonText, setJsonText] = useState("");
  const [isCopying, setIsCopying] = useState(false);
  useEffect(() => {
    try {
      const messagesJson = JSON.stringify(messages, null, 2);
      setJsonText(messagesJson);
    } catch (error) {
      console.error("Error serializing messages:", error);
    }
  }, [messages]);
  const handleChange = useCallback(
    (event) => {
      setJsonText(event.target.value);
    },
    []
  );
  const handleBlur = useCallback(() => {
    try {
      const parsedMessages = JSON.parse(jsonText);
      if (Array.isArray(parsedMessages)) {
        setMessages(parsedMessages);
      } else {
        console.error("Invalid messages format: not an array");
      }
    } catch (error) {
      console.error("Error parsing JSON:", error);
    }
  }, [jsonText, setMessages]);
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, [setMessages]);
  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      setIsCopying(true);
      setTimeout(() => setIsCopying(false), 2e3);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  }, [jsonText]);
  return /* @__PURE__ */ jsxs("div", { className: styles$5.container, children: [
    /* @__PURE__ */ jsxs("div", { className: styles$5.actionsContainer, children: [
      /* @__PURE__ */ jsx(Tooltip, { content: "Clear all messages", children: /* @__PURE__ */ jsx(
        IconButton,
        {
          name: "formXCircleOutlined",
          "aria-label": "Clear all messages",
          variant: "ghost",
          size: "small",
          onPress: clearMessages
        }
      ) }),
      /* @__PURE__ */ jsx(Tooltip, { content: isCopying ? "Copied!" : "Copy to clipboard", children: /* @__PURE__ */ jsx(
        IconButton,
        {
          name: isCopying ? "formCheck" : "actionCopy",
          "aria-label": isCopying ? "Copied!" : "Copy to clipboard",
          variant: "ghost",
          size: "small",
          onPress: copyToClipboard
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx(
      "textarea",
      {
        className: styles$5.textarea,
        value: jsonText,
        onChange: handleChange,
        onBlur: handleBlur,
        spellCheck: false,
        "aria-label": "Chat messages JSON"
      }
    ),
    /* @__PURE__ */ jsx("div", { className: styles$5.footer, children: conversationId && spanId && braintrustProjectName && /* @__PURE__ */ jsxs("div", { className: styles$5.footerSection, children: [
      /* @__PURE__ */ jsx("span", { className: styles$5.footerLabel, children: "Thread ID:" }),
      /* @__PURE__ */ jsx(
        "a",
        {
          href: getBraintrustLogsUrl(
            conversationId,
            spanId,
            braintrustProjectName
          ),
          target: "_blank",
          rel: "noopener noreferrer",
          className: styles$5.braintrustLink,
          children: conversationId
        }
      )
    ] }) })
  ] });
}
ViewMessagesJSON.displayName = "ViewMessagesJSON";

const section = "_section_1480g_1";
const header = "_header_1480g_5";
const styles$4 = {
	section: section,
	header: header
};

const ItemGroup = ({ label, children }) => {
  return /* @__PURE__ */ jsxs(Section, { className: styles$4.section, children: [
    /* @__PURE__ */ jsx(Header$1, { className: styles$4.header, children: label }),
    children
  ] });
};

const menu = "_menu_15m97_1";
const styles$3 = {
	menu: menu
};

const menuItem = "_menu-item_1eytr_1";
const styles$2 = {
	menuItem: menuItem
};

const MenuItem = ({
  children,
  iconLeft,
  ...menuItemProps
}) => {
  return /* @__PURE__ */ jsxs(MenuItem$1, { className: styles$2.menuItem, ...menuItemProps, children: [
    iconLeft && /* @__PURE__ */ jsx(Icon$1, { size: 18, name: iconLeft }),
    /* @__PURE__ */ jsx("div", { children })
  ] });
};

const separator = "_separator_pdocg_1";
const styles$1 = {
	separator: separator
};

const Separator = () => /* @__PURE__ */ jsx(Separator$1, { className: styles$1.separator });

const MenuTrigger = ({ children }) => {
  return /* @__PURE__ */ jsx(MenuTrigger$1, { children });
};
const Menu = ({ placement, offset, ...menuProps }) => {
  return /* @__PURE__ */ jsx(Popover, { placement, offset, children: /* @__PURE__ */ jsx(Menu$1, { ...menuProps, className: styles$3.menu }) });
};
Menu.Item = MenuItem;
Menu.Trigger = MenuTrigger;
Menu.ItemGroup = ItemGroup;
Menu.Separator = Separator;

const chatPanel = "_chat-panel_vyrwv_1";
const headerContainer = "_header-container_vyrwv_8";
const chatPanelContent = "_chat-panel-content_vyrwv_18";
const styles = {
	chatPanel: chatPanel,
	headerContainer: headerContainer,
	chatPanelContent: chatPanelContent};

function CopilotChatPanel({
  onCloseClick,
  inputProps = {},
  messageListProps = {},
  placeholder,
  onChatReset,
  headerProps = {}
}) {
  const {
    components,
    conversationId,
    error,
    getHandoffData,
    handleInputChange,
    handleSubmit,
    input,
    messages,
    copilotId,
    resetChat,
    setInput,
    status,
    stop,
    isDebug,
    addToolResult,
    reload
  } = useCopilotChat();
  const [isJsonMode, setIsJsonMode] = useState(false);
  const chatPanelRef = useRef(null);
  const renderPart = useCallback(
    (message, part) => {
      if (part.type === "tool-invocation" && components[part.toolInvocation.toolName]) {
        return /* @__PURE__ */ jsx(
          ToolPart,
          {
            part,
            components,
            message,
            addToolResult,
            messages,
            conversationId,
            getHandoffData
          }
        );
      }
      return void 0;
    },
    [components, messages, conversationId, getHandoffData, addToolResult]
  );
  const trackInteractionEvent = useCallback(
    ({
      interactionName,
      eventAction
    }) => {
      const commonFields = getCommonTrackingEventFields({
        conversationId,
        copilotId,
        userMessageId: messages.slice().reverse().find((m) => m.role === "user")?.id
        // TODO: Add customUserId and accountId in EAI-1766
      });
      trackCopilotInteractionEvent({
        interaction_name: snakeCase(interactionName),
        interaction_goal: snakeCase(interactionName),
        interaction_raw_text: interactionName,
        event_action: eventAction,
        subject: "chat",
        ...commonFields
      });
    },
    [conversationId, copilotId, messages]
  );
  const handleClearChat = useCallback(() => {
    const chatResetOptions = onChatReset?.() ?? {};
    resetChat(chatResetOptions);
    trackInteractionEvent({
      interactionName: "Clear chat",
      eventAction: "click"
    });
  }, [onChatReset, resetChat, trackInteractionEvent]);
  const handleCloseCopilot = useCallback(
    (event) => {
      onCloseClick?.(event);
      trackInteractionEvent({
        interactionName: "Close Copilot",
        eventAction: "close"
      });
    },
    [onCloseClick, trackInteractionEvent]
  );
  return /* @__PURE__ */ jsxs("div", { ref: chatPanelRef, className: styles.chatPanel, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: clsx$1(styles.headerContainer, headerProps.containerClassName),
        style: headerProps.containerStyle,
        children: /* @__PURE__ */ jsxs(Header, { className: headerProps.className, style: headerProps.style, children: [
          /* @__PURE__ */ jsxs(Menu.Trigger, { children: [
            /* @__PURE__ */ jsx(Tooltip, { content: "Settings", children: /* @__PURE__ */ jsx(
              IconButton,
              {
                name: "navCog",
                variant: "ghost",
                size: "small",
                "aria-label": "Copilot Settings"
              }
            ) }),
            /* @__PURE__ */ jsx(Menu, { offset: -1, placement: "bottom end", children: /* @__PURE__ */ jsx(
              Menu.Item,
              {
                iconLeft: "formXCircleOutlined",
                onAction: handleClearChat,
                children: "Clear chat"
              }
            ) })
          ] }),
          onCloseClick && /* @__PURE__ */ jsx(Tooltip, { content: "Close", children: /* @__PURE__ */ jsx(
            IconButton,
            {
              name: "arrowSmallLeft",
              "aria-label": "Close Copilot",
              variant: "tertiary",
              size: "small",
              onPress: handleCloseCopilot
            }
          ) })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: styles.chatPanelContent, children: [
      isJsonMode ? /* @__PURE__ */ jsx(ViewMessagesJSON, {}) : /* @__PURE__ */ jsx(
        CopilotMessageList,
        {
          ...messageListProps,
          messages,
          renderPart,
          status,
          error: error ? {
            message: error.message || "An error occurred",
            header: error.header,
            onRetry: reload ? () => reload() : void 0
          } : void 0
        }
      ),
      /* @__PURE__ */ jsx(
        CopilotInput,
        {
          ...inputProps,
          input,
          handleInputChange,
          handleSubmit,
          setInput,
          stop,
          status,
          placeholder: placeholder || inputProps.placeholder || "Type a message...",
          jsonModeState: [isJsonMode, setIsJsonMode],
          isDebug,
          dragDropContainerRef: chatPanelRef
        }
      )
    ] })
  ] });
}

export { CopilotAlert, CopilotAssistantMessagePartWrapper, CopilotChatPanel, CopilotFilePart, CopilotIcon, CopilotInput, CopilotInterruptionAlert, CopilotMarkdown, CopilotMessage, CopilotMessageList, CopilotMessagePart, CopilotMessagePartWrapper, CopilotMode, CopilotProvider, CopilotTextPart, CopilotToolPart, CopilotUserMessagePartWrapper, FeedbackModal, FileAttachment, FileAttachmentList, FileListDisplay, FileUploadButton, FileUploadControls, HANDOFF_TOOL_NAME, HandoffReceived, HandoffSuggestion, Header, ImageLightbox, SPAN_ID_HEADERS, Thinking, Thought, ToolPart, ViewMessagesJSON, commonToolParams, convertFileUploadStateToFileList, createCopilotRegistry, createDefaultHandoffComponent, createFeedbackCallback, createOnToolCall, defaultGetToolCallDisplay, defaultHandoffStorage, extractComponents, getCommonTrackingEventFields, handleSubmitWithFileAttachments, makeClientTool, makeHandoffTool, makeToolEffect, processToolEffects, serializeTools, submitFeedback, trackCopilotInteractionEvent, useConversationId, useCopilotChat, useCopilotContext, useCopilotRegistry, useFeedbackConfig, useFeedbackModal, useFileUploadState, useHandoff, useSpanId, useToolEffects };
//# sourceMappingURL=client.es.js.map
